import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { toIsoDate, parseIsoDate, formatDate } from "../lib/dates";

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY || "";
const ADMIN_ID = import.meta.env.VITE_ADMIN_USER_ID || "";

const VEHICLES = [
  { id:"sedan", label:"Sedan / Coupe" },
  { id:"suv",   label:"SUV / Crossover" },
  { id:"truck", label:"Truck" },
  { id:"van",   label:"Van / Minivan" },
];

const css = `
  .dash-shell { min-height:100vh; min-height:100dvh; display:flex; flex-direction:column; }

  /* TOP NAV */
  .dash-nav { display:flex; align-items:center; justify-content:space-between; padding:14px 28px; padding-left:max(28px, env(safe-area-inset-left)); padding-right:max(28px, env(safe-area-inset-right)); background:var(--surface); border-bottom:1px solid var(--border); position:sticky; top:0; z-index:100; }
  .dash-nav-logo { font-family:var(--head); font-size:22px; font-weight:800; letter-spacing:2px; }
  .dash-nav-logo span { color:var(--blue); }
  .dash-nav-right { display:flex; align-items:center; gap:12px; }
  .dash-nav-tabs { display:flex; gap:4px; background:var(--card); border-radius:10px; padding:3px; }
  .dash-tab { padding:7px 18px; border-radius:7px; border:none; cursor:pointer; font-family:var(--body); font-size:13px; font-weight:500; transition:all .2s; background:transparent; color:var(--muted); }
  .dash-tab.active { background:var(--blue); color:#fff; }

  /* CONTENT */
  .dash-content { flex:1; padding:28px; padding-left:max(28px, env(safe-area-inset-left)); padding-right:max(28px, env(safe-area-inset-right)); max-width:1200px; width:100%; margin:0 auto; }

  /* STATS */
  .stats-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:14px; margin-bottom:28px; }
  .stat-card  { background:var(--card); border:1px solid var(--border); border-radius:14px; padding:20px; }
  .stat-label { font-size:11px; font-weight:700; letter-spacing:2px; color:var(--muted); text-transform:uppercase; margin-bottom:8px; }
  .stat-val   { font-family:var(--head); font-size:36px; font-weight:800; }
  .stat-val.blue  { color:var(--blue); }
  .stat-val.teal  { color:var(--teal); }
  .stat-val.warn  { color:var(--warn); }
  .stat-sub   { color:var(--muted); font-size:12px; margin-top:4px; }

  /* BOOKING ROWS */
  .appt-row { display:flex; align-items:center; gap:14px; padding:14px 20px; border-bottom:1px solid var(--border); transition:background .2s; cursor:pointer; }
  .appt-row:hover { background:var(--card2); }
  .appt-row:last-child { border-bottom:none; }
  .appt-avatar { width:40px; height:40px; border-radius:50%; background:linear-gradient(135deg,var(--blue),var(--teal)); display:flex; align-items:center; justify-content:center; font-weight:700; font-size:14px; color:#fff; flex-shrink:0; }
  .appt-info { flex:1; min-width:0; }
  .appt-name { font-weight:600; font-size:14px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .appt-sub  { font-size:12px; color:var(--muted); margin-top:2px; }
  .appt-right { text-align:right; flex-shrink:0; }
  .appt-amount { font-family:var(--head); font-size:18px; font-weight:700; color:var(--teal); }
  .appt-time   { font-size:11px; color:var(--muted); margin-top:2px; }

  /* SERVICES */
  .svc-list { display:flex; flex-direction:column; gap:12px; }
  .svc-card { background:var(--card); border:1px solid var(--border); border-radius:14px; padding:18px 20px; display:flex; align-items:center; gap:16px; }
  .svc-icon { font-size:28px; flex-shrink:0; }
  .svc-info { flex:1; min-width:0; }
  .svc-name { font-family:var(--head); font-size:18px; font-weight:700; letter-spacing:1px; }
  .svc-desc { font-size:13px; color:var(--muted); margin-top:2px; }
  .svc-prices { font-size:12px; color:var(--muted); margin-top:4px; }
  .svc-actions { display:flex; gap:8px; flex-shrink:0; }
  .btn-edit   { background:var(--card2); border:1px solid var(--border); border-radius:8px; padding:7px 14px; font-family:var(--body); font-size:13px; color:var(--muted); cursor:pointer; transition:.2s; }
  .btn-edit:hover { border-color:var(--blue); color:var(--blue); }
  .btn-del    { background:transparent; border:1px solid var(--border); border-radius:8px; padding:7px 12px; font-size:13px; color:var(--muted); cursor:pointer; transition:.2s; }
  .btn-del:hover  { border-color:var(--danger); color:var(--danger); }
  .plan-gate  { background:rgba(59,130,246,.06); border:1px dashed rgba(59,130,246,.3); border-radius:14px; padding:24px; text-align:center; }
  .plan-gate p { color:var(--muted); font-size:14px; margin-bottom:14px; }

  /* AVAILABILITY */
  .avail-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; }
  @media (max-width: 740px) { .avail-grid { grid-template-columns:1fr; gap:14px; } }
  .avail-cal-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:6px; margin-bottom:8px; }
  .avail-day-label { text-align:center; font-size:11px; font-weight:600; color:var(--muted); padding:4px 0; }
  .avail-day { aspect-ratio:1; border:none; border-radius:8px; background:var(--card); color:var(--text); font-family:var(--body); font-size:13px; cursor:pointer; transition:all .2s; border:2px solid transparent; display:flex; align-items:center; justify-content:center; }
  .avail-day:hover { border-color:var(--blue); }
  .avail-day.today { border-color:var(--teal); color:var(--teal); font-weight:700; }
  .avail-day.past  { opacity:.25; cursor:not-allowed; }
  .avail-day.blocked { background:rgba(239,68,68,.15); border-color:rgba(239,68,68,.4); color:var(--danger); }
  .avail-day.has-booking { background:rgba(59,130,246,.12); border-color:rgba(59,130,246,.3); color:var(--blue); }
  .avail-day.selected { background:var(--blue); color:#fff; border-color:var(--blue); }
  .avail-legend { display:flex; gap:16px; flex-wrap:wrap; margin-top:12px; }
  .avail-legend-item { display:flex; align-items:center; gap:6px; font-size:12px; color:var(--muted); }
  .avail-legend-dot { width:10px; height:10px; border-radius:3px; flex-shrink:0; }

  /* PHOTOS */
  .photo-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:10px; }
  .photo-thumb { aspect-ratio:1; border-radius:10px; overflow:hidden; position:relative; background:var(--card2); border:1px solid var(--border); }
  .photo-thumb img { width:100%; height:100%; object-fit:cover; }
  .photo-thumb .photo-del { position:absolute; top:4px; right:4px; background:rgba(0,0,0,.7); border:none; border-radius:50%; width:22px; height:22px; color:#fff; font-size:11px; cursor:pointer; display:flex; align-items:center; justify-content:center; opacity:0; transition:.2s; }
  .photo-thumb:hover .photo-del { opacity:1; }
  .photo-upload-btn { aspect-ratio:1; border-radius:10px; border:2px dashed var(--border); background:transparent; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; cursor:pointer; transition:.2s; color:var(--muted); font-size:12px; }
  .photo-upload-btn:hover { border-color:var(--blue); color:var(--blue); }
  .photo-type-badge { position:absolute; bottom:4px; left:4px; background:rgba(0,0,0,.7); color:#fff; font-size:10px; font-weight:600; padding:2px 6px; border-radius:4px; }

  /* MODAL DETAILS */
  .modal-row { display:flex; justify-content:space-between; padding:10px 0; border-bottom:1px solid var(--border); font-size:14px; }
  .modal-row:last-of-type { border-bottom:none; }
  .modal-actions { display:flex; gap:10px; margin-top:20px; flex-wrap:wrap; }
  .btn-confirm { flex:1; background:var(--success); color:#fff; border:none; border-radius:10px; padding:12px; font-family:var(--body); font-weight:600; cursor:pointer; transition:.2s; }
  .btn-confirm:hover { background:#16a34a; }
  .btn-decline { flex:1; background:rgba(239,68,68,.1); color:var(--danger); border:1px solid rgba(239,68,68,.3); border-radius:10px; padding:12px; font-family:var(--body); font-weight:600; cursor:pointer; transition:.2s; }
  .btn-decline:hover { background:rgba(239,68,68,.2); }
  .btn-close   { background:transparent; border:1px solid var(--border); border-radius:10px; padding:12px 20px; font-family:var(--body); font-weight:500; color:var(--muted); cursor:pointer; transition:.2s; }
  .btn-close:hover { border-color:var(--blue); color:var(--text); }

  /* PRICING GRID IN MODAL */
  .pricing-fields { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .empty-state { text-align:center; padding:40px 20px; color:var(--muted); }
  .empty-state .e-icon { font-size:40px; margin-bottom:12px; }

  /* BOOKING LINK BOX */
  .link-box { display:flex; align-items:center; gap:10px; background:var(--card2); border:1px solid var(--border); border-radius:10px; padding:12px 16px; }
  .link-box span { flex:1; font-size:14px; color:var(--teal); font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

  /* CUSTOMERS */
  .customer-row { display:flex; align-items:center; gap:14px; padding:14px 20px; border-bottom:1px solid var(--border); cursor:pointer; transition:background .2s; }
  .customer-row:hover { background:var(--card2); }
  .customer-row:last-child { border-bottom:none; }
  .cust-info { flex:1; min-width:0; }
  .cust-name { font-weight:600; font-size:14px; }
  .cust-sub  { font-size:12px; color:var(--muted); margin-top:2px; }
  .cust-right { text-align:right; flex-shrink:0; }
  .cust-spent { font-family:var(--head); font-size:16px; font-weight:700; color:var(--teal); }
  .cust-jobs  { font-size:11px; color:var(--muted); margin-top:2px; }

  /* INVENTORY (Pro+) */
  .inv-list { display:flex; flex-direction:column; gap:12px; }
  .inv-card { background:var(--card); border:1px solid var(--border); border-radius:14px; padding:16px 18px; display:flex; align-items:center; gap:14px; }
  .inv-card.low { border-color:rgba(239,68,68,.5); background:linear-gradient(180deg,rgba(239,68,68,.04),transparent); }
  .inv-info { flex:1; min-width:0; }
  .inv-name { font-family:var(--head); font-size:16px; font-weight:700; letter-spacing:1px; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .inv-meta { font-size:12px; color:var(--muted); margin-top:3px; }
  .inv-low-chip { font-size:10px; font-weight:700; letter-spacing:1px; color:#fff; background:#ef4444; border-radius:4px; padding:2px 7px; }
  .inv-qty-block { display:flex; align-items:center; gap:8px; flex-shrink:0; }
  .inv-qty-btn { background:var(--card2); border:1px solid var(--border); color:var(--text); width:34px; height:34px; border-radius:8px; font-size:18px; font-weight:700; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:border-color .15s, background .15s; }
  .inv-qty-btn:hover:not(:disabled) { border-color:var(--blue); background:rgba(59,130,246,.08); }
  .inv-qty-btn:disabled { opacity:.4; cursor:not-allowed; }
  .inv-qty-val { font-family:var(--head); font-size:22px; font-weight:800; min-width:48px; text-align:center; color:var(--teal); }
  .inv-qty-unit { font-size:11px; color:var(--muted); text-transform:uppercase; letter-spacing:1px; }
  .inv-actions { display:flex; gap:6px; flex-shrink:0; }
  @media(max-width:640px){ .inv-card { flex-wrap:wrap; } .inv-info { width:100%; } }

  /* REVENUE */
  .goal-row { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:8px; }
  .goal-track { width:100%; height:10px; background:var(--card2); border-radius:999px; overflow:hidden; }
  .goal-fill { height:100%; background:linear-gradient(90deg,var(--teal),var(--blue)); transition:width .4s ease; }
  .goal-edit { background:transparent; border:1px solid var(--border); color:var(--muted); border-radius:8px; padding:4px 10px; font-size:11px; cursor:pointer; }
  .goal-edit:hover { color:var(--text); border-color:var(--text); }
  .goal-input { width:120px; background:var(--card2); border:1px solid var(--border); border-radius:8px; padding:6px 10px; color:var(--text); font-family:var(--body); font-size:13px; outline:none; }
  .goal-input:focus { border-color:var(--blue); }

  /* EARNINGS */
  .earnings-bar-wrap { background:var(--card2); border-radius:8px; height:8px; overflow:hidden; margin-top:6px; }
  .earnings-bar { height:100%; background:var(--teal); border-radius:8px; transition:width .6s ease; }
  .payout-row { display:flex; align-items:center; justify-content:space-between; padding:12px 0; border-bottom:1px solid var(--border); font-size:14px; }
  .payout-row:last-child { border-bottom:none; }

  /* MAP */
  .map-container { width:100%; height:320px; border-radius:12px; overflow:hidden; border:1px solid var(--border); background:var(--card2); position:relative; }
  .map-placeholder { width:100%; height:100%; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; color:var(--muted); font-size:14px; }

  /* BOTTOM NAV (mobile only) */
  .bottom-nav { display:none; }

  /* ── MOBILE ─────────────────────────────────────────── */
  @media (max-width: 640px) {
    /* Nav: hide tabs + sign out, show only logo + badge */
    .dash-nav { padding:12px 16px; }
    .dash-nav-tabs { display:none; }
    .dash-nav-right .btn-ghost { display:none; }
    .dash-nav-right .btn-edit  { display:none; }

    /* Bottom nav */
    .bottom-nav {
      display:flex; position:fixed; bottom:0; left:0; right:0; z-index:200;
      background:var(--surface); border-top:1px solid var(--border);
      padding:6px max(env(safe-area-inset-right), 4px) max(20px, env(safe-area-inset-bottom)) max(env(safe-area-inset-left), 4px);
    }
    .bottom-nav-item {
      flex:1; display:flex; flex-direction:column; align-items:center; gap:2px;
      background:none; border:none; cursor:pointer; padding:4px 2px;
      color:var(--muted); font-family:var(--body); font-size:9px; font-weight:500;
      letter-spacing:.3px; text-transform:uppercase; transition:color .2s;
      min-height:44px; justify-content:center;
    }
    .bottom-nav-item.active { color:var(--blue); }
    .bottom-nav-icon { font-size:18px; line-height:1; }

    /* Content: account for bottom nav + reduce padding */
    .dash-content { padding:16px max(14px, env(safe-area-inset-right)) 100px max(14px, env(safe-area-inset-left)); }

    /* Stats: 2×2 grid */
    .stats-grid { grid-template-columns:1fr 1fr; gap:10px; margin-bottom:18px; }
    .stat-card  { padding:14px; }
    .stat-val   { font-size:28px; }

    /* Hide booking link in overview header */
    .mobile-hide { display:none !important; }

    /* Booking rows: bigger tap target */
    .appt-row { padding:14px 12px; min-height:44px; }
    .appt-amount { font-size:16px; }

    /* Service cards: wrap actions below on narrow */
    .svc-card { flex-wrap:wrap; gap:10px; padding:14px; }
    .svc-actions { width:100%; justify-content:flex-end; }
    .btn-edit, .btn-del { min-height:40px; }

    /* Settings */
    .settings-grid { max-width:100%; }

    /* Modals: full screen bottom sheet */
    .modal-overlay { align-items:flex-end; padding:0; }
    .modal {
      width:100%; max-width:100%; border-radius:20px 20px 0 0;
      max-height:92vh; overflow-y:auto; padding:24px 16px 32px;
      margin:0;
    }

    /* Pricing fields: single column on mobile */
    .pricing-fields { grid-template-columns:1fr; }

    /* Sticky save button in settings */
    .settings-save-sticky {
      position:sticky; bottom:0; background:var(--surface);
      padding:12px 0 4px; margin-top:8px;
      border-top:1px solid var(--border);
    }

    /* Customers */
    .customer-row { padding:12px; }
    .detail-grid-mobile { grid-template-columns:1fr !important; }

    /* Map */
    .map-container { height:220px; }
  }
`;

const fmt$ = n => `$${n}`;
const initials = name => name?.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase() || "?";

// WMO weather code → emoji + label. Open-Meteo returns these codes.
function wxFromCode(code) {
  if (code === 0)                    return { icon: "☀️",  label: "Clear sky"        };
  if (code === 1 || code === 2)      return { icon: "🌤️",  label: code === 1 ? "Mainly clear" : "Partly cloudy" };
  if (code === 3)                    return { icon: "☁️",  label: "Overcast"         };
  if (code === 45 || code === 48)    return { icon: "🌫️",  label: "Foggy"            };
  if (code >= 51 && code <= 55)      return { icon: "🌦️",  label: "Drizzle"          };
  if (code === 56 || code === 57)    return { icon: "🌧️",  label: "Freezing drizzle" };
  if (code >= 61 && code <= 65)      return { icon: "🌧️",  label: "Rain"             };
  if (code === 66 || code === 67)    return { icon: "🌨️",  label: "Freezing rain"    };
  if (code >= 71 && code <= 77)      return { icon: "❄️",  label: "Snow"             };
  if (code >= 80 && code <= 82)      return { icon: "🌧️",  label: "Rain showers"     };
  if (code === 85 || code === 86)    return { icon: "❄️",  label: "Snow showers"     };
  if (code === 95)                   return { icon: "⛈️",  label: "Thunderstorm"     };
  if (code === 96 || code === 99)    return { icon: "⛈️",  label: "Hail storm"       };
  return { icon: "🌡️", label: "Unknown" };
}

// True for codes that mean "actual precipitation falling" (vs. just overcast/fog).
const isPrecipCode = c => (c >= 51 && c <= 67) || (c >= 71 && c <= 77) || (c >= 80 && c <= 86) || (c >= 95 && c <= 99);

function Toast({ msg, type }) {
  if (!msg) return null;
  return <div className={`toast ${type}`}>{msg}</div>;
}

export default function Dashboard({ session }) {
  const navigate = useNavigate();
  const [tab, setTab]               = useState("overview");
  const [detailer, setDetailer]     = useState(null);
  const [bookings, setBookings]     = useState([]);
  const [services, setServices]     = useState([]);
  const [customers, setCustomers]   = useState([]);
  const [blockedDates, setBlockedDates] = useState([]); // array of "Month D, YYYY" strings
  const [availCalYear, setAvailCalYear] = useState(new Date().getFullYear());
  const [availCalMonth, setAvailCalMonth] = useState(new Date().getMonth());
  const [blockingDate, setBlockingDate] = useState(null);
  const [photos, setPhotos]         = useState([]); // {id, url, booking_id, type, created_at}
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [smsForm, setSmsForm]       = useState({ phone:"", message:"" });
  const [sendingSms, setSendingSms] = useState(false);
  const [editingGoals, setEditingGoals]   = useState(false);
  const [goalWeeklyInput, setGoalWeeklyInput]   = useState("");
  const [goalMonthlyInput, setGoalMonthlyInput] = useState("");
  const [savingGoals, setSavingGoals]     = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [onboardForm, setOnboardForm] = useState({ bizName:"", slug:"", phone:"", city:"", state:"" });
  const [slugStatus, setSlugStatus]   = useState(null);
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [onboardError, setOnboardError]     = useState(null);
  const [toast, setToast]           = useState(null);

  // Modals
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [svcModal, setSvcModal]         = useState(null); // null | "new" | service object
  const [inventory, setInventory]       = useState([]);
  const [invModal, setInvModal]         = useState(null); // null | "new" | inventory item object
  const [invForm, setInvForm]           = useState({ name:"", quantity:"", unit:"each", cost_per_unit:"", low_stock_threshold:"", notes:"" });
  const [updating, setUpdating]         = useState(false);

  // AI Quotes (Pro+)
  const [aiQuotes, setAiQuotes]                 = useState([]);
  const [pricingRules, setPricingRules]         = useState(null); // null until loaded; object once detailer is hydrated
  const [savingPricingRules, setSavingPricingRules] = useState(false);
  const [expandedQuoteId, setExpandedQuoteId]   = useState(null);


  // Job flow
  const [activeJob, setActiveJob]     = useState(null);   // booking being worked on
  const [jobStep, setJobStep]         = useState("arrival"); // "arrival"|"before"|"after"|"payment"
  const [adjustmentReasonInput, setAdjustmentReasonInput] = useState("");
  const [savingAdjustment, setSavingAdjustment]           = useState(false);
  const [editingPrice, setEditingPrice]                   = useState(false);
  const [priceEditValue, setPriceEditValue]               = useState("");
  const [editingApptPrice, setEditingApptPrice]           = useState(false);
  const [apptPriceEditValue, setApptPriceEditValue]       = useState("");
  const [aiEstimate, setAiEstimate]                       = useState(null);
  const [estimatePrice, setEstimatePrice]                 = useState("");
  const [estimateHours, setEstimateHours]                 = useState("");
  const [generatingEstimate, setGeneratingEstimate]       = useState(false);
  const [arrivalSent, setArrivalSent] = useState(false);
  const [paymentUrl, setPaymentUrl]           = useState(null);
  const [generatingPayLink, setGeneratingPayLink] = useState(false);
  const [payLinkMethod, setPayLinkMethod]     = useState(null); // "stripe" | "square"
  // Service form state
  const [svcForm, setSvcForm] = useState({ name:"", description:"", icon:"✨", duration:"", pricing:{ sedan:"", suv:"", truck:"", van:"" } });

  const showToast = (msg, type="success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const toSlug = name => name.toLowerCase().replace(/[^a-z0-9\s-]/g,"").trim().replace(/\s+/g,"-").slice(0,40);

  const checkOnboardSlug = async val => {
    const s = toSlug(val);
    setOnboardForm(f => ({ ...f, slug:s }));
    if (!s) { setSlugStatus(null); return; }
    setSlugStatus("checking");
    const { data } = await supabase.from("detailers").select("id").eq("slug", s).maybeSingle();
    setSlugStatus(data ? "taken" : "ok");
  };

  // Capped at 3 to match the free plan limit; new detailers can add more after upgrading.
  const DEFAULT_SERVICES = [
    { name:"Basic Wash",       description:"Exterior hand wash, windows, tire shine",            icon:"💧", duration:"1–1.5 hrs", pricing:{ sedan:40,  suv:55,  truck:60,  van:65  } },
    { name:"Interior Detail",  description:"Full vacuum, wipe down, leather conditioning",       icon:"✨", duration:"2–3 hrs",   pricing:{ sedan:80,  suv:100, truck:100, van:110 } },
    { name:"Full Detail",      description:"Complete interior + exterior detail package",        icon:"🏆", duration:"4–6 hrs",   pricing:{ sedan:150, suv:180, truck:185, van:200 } },
  ];

  const finishOnboarding = async e => {
    e.preventDefault();
    if (slugStatus === "taken") { setOnboardError("That URL is already taken."); return; }
    setOnboardLoading(true); setOnboardError(null);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setOnboardError("Session expired. Please sign in again."); setOnboardLoading(false); return; }

    const { data: det, error: dErr } = await supabase.from("detailers")
      .insert([{ user_id: user.id, business_name: onboardForm.bizName, slug: onboardForm.slug, phone: onboardForm.phone, email: user.email, city: onboardForm.city, state: onboardForm.state }])
      .select().single();

    if (dErr) { setOnboardError(dErr.message); setOnboardLoading(false); return; }

    for (let i = 0; i < DEFAULT_SERVICES.length; i++) {
      const svc = DEFAULT_SERVICES[i];
      const { data: svcRow } = await supabase.from("services")
        .insert([{ detailer_id: det.id, name: svc.name, description: svc.description, icon: svc.icon, duration: svc.duration, sort_order: i }])
        .select().single();
      if (svcRow) {
        await supabase.from("service_pricing").insert(
          Object.entries(svc.pricing).map(([vehicle_type, price]) => ({ service_id: svcRow.id, vehicle_type, price }))
        );
      }
    }

    setNeedsOnboarding(false);
    loadAll();
    setOnboardLoading(false);
  };
  const isPro     = detailer?.plan === "pro" || detailer?.plan === "pro_plus";
  const isProPlus = detailer?.plan === "pro_plus";
  const isTrialing = detailer?.subscription_status === "trialing";

  const trialDaysLeft = (() => {
    if (!isTrialing || !detailer?.trial_end) return null;
    const diff = new Date(detailer.trial_end) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  })();
  const activeServices = services.filter(s => s.active !== false);
  const canAddService = isPro || activeServices.length < 3;

  const [billingCycle, setBillingCycle] = useState("monthly");
  const PRICE_MONTHLY = import.meta.env.VITE_STRIPE_PRICE_MONTHLY;
  const PRICE_ANNUAL  = import.meta.env.VITE_STRIPE_PRICE_ANNUAL;

  // ── LOAD DATA ──────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoadingData(true);
    const { data: det } = await supabase.from("detailers").select("*").eq("user_id", session.user.id).single();
    if (!det) { setNeedsOnboarding(true); setLoadingData(false); return; }
    setNeedsOnboarding(false);
    setDetailer(det);

    const isProPlusPlan = det.plan === "pro_plus";
    const [{ data: bkgs }, { data: svcs }, { data: custs }, { data: blocked }, { data: phs }, invRes, quotesRes] = await Promise.all([
      supabase.from("bookings").select("*").eq("detailer_id", det.id).order("created_at", { ascending:false }),
      supabase.from("services").select("*, service_pricing(*)").eq("detailer_id", det.id).order("sort_order"),
      supabase.from("customers").select("*").eq("detailer_id", det.id).order("created_at", { ascending:false }),
      supabase.from("blocked_times").select("*").eq("detailer_id", det.id),
      supabase.from("job_photos").select("*").eq("detailer_id", det.id).order("created_at", { ascending:false }),
      // Pro+-only tables: skip the query for other plans so non-Pro+ detailers
      // don't 401/empty-load against gated tables.
      isProPlusPlan
        ? supabase.from("inventory_items").select("*").eq("detailer_id", det.id).order("created_at", { ascending:false })
        : Promise.resolve({ data: [] }),
      isProPlusPlan
        ? supabase.from("ai_quotes").select("*").eq("detailer_id", det.id).order("created_at", { ascending:false })
        : Promise.resolve({ data: [] }),
    ]);
    setBookings(bkgs ?? []);
    setServices(svcs ?? []);
    setCustomers(custs ?? []);
    setInventory(invRes?.data ?? []);
    setAiQuotes(quotesRes?.data ?? []);
    setPricingRules(det.quote_pricing_rules || null);
    setBlockedDates((blocked ?? []).map(b => b.date));
    setPhotos(phs ?? []);
    setLoadingData(false);
  }, [session.user.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── WEATHER (Pro) ──────────────────────────────────
  const [weather, setWeather]               = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  useEffect(() => {
    if (!isPro || !detailer?.id) return;
    let cancelled = false;
    const fetchWeather = async () => {
      setWeatherLoading(true);
      let lat = detailer.service_lat;
      let lng = detailer.service_lng;
      const locationLabel = detailer.city
        ? `${detailer.city}${detailer.state ? `, ${detailer.state}` : ""}`
        : "your area";

      // Fall back to Open-Meteo's free geocoding API (no key) when we don't
      // have lat/lng yet — typical for detailers who haven't set their
      // service area in Settings.
      if ((lat == null || lng == null) && detailer.city) {
        try {
          const q = encodeURIComponent(`${detailer.city}${detailer.state ? `, ${detailer.state}` : ""}`);
          const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${q}&count=1`);
          const data = await res.json();
          if (data.results?.[0]) {
            lat = data.results[0].latitude;
            lng = data.results[0].longitude;
          }
        } catch { /* silent */ }
      }
      if (lat == null || lng == null) { if (!cancelled) setWeatherLoading(false); return; }

      try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
          `&current=temperature_2m,weather_code` +
          `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max` +
          `&temperature_unit=fahrenheit&forecast_days=6&timezone=auto`;
        const res  = await fetch(url);
        const data = await res.json();
        if (!cancelled) setWeather({ ...data, locationLabel });
      } catch { /* silent — weather is non-critical */ }
      if (!cancelled) setWeatherLoading(false);
    };
    fetchWeather();
    return () => { cancelled = true; };
  }, [isPro, detailer?.id, detailer?.service_lat, detailer?.service_lng, detailer?.city, detailer?.state]);

  // ── PER-BOOKING WEATHER (Pro+) ────────────────────
  const [bookingWeather, setBookingWeather] = useState({});
  const fetchedWeatherIds = useRef(new Set());

  const wmoIcon = (code) => {
    if (code === 0)            return "☀️";
    if (code <= 2)             return "🌤️";
    if (code === 3)            return "☁️";
    if (code <= 48)            return "🌫️";
    if (code <= 55)            return "🌦️";
    if (code <= 67)            return "🌧️";
    if (code <= 77)            return "🌨️";
    if (code <= 82)            return "🌧️";
    if (code <= 86)            return "🌨️";
    return "⛈️";
  };

  useEffect(() => {
    if (!isProPlus || !bookings.length) return;
    let cancelled = false;
    const today   = new Date().toISOString().split("T")[0];
    const cutoff  = new Date(); cutoff.setDate(cutoff.getDate() + 14);
    const cutoffStr = cutoff.toISOString().split("T")[0];
    const upcoming = bookings.filter(b =>
      b.date >= today && b.date <= cutoffStr && b.address &&
      b.status !== "cancelled" && b.status !== "declined"
    );
    const fetchAll = async () => {
      for (const b of upcoming) {
        if (cancelled) break;
        if (fetchedWeatherIds.current.has(b.id)) continue;
        fetchedWeatherIds.current.add(b.id);
        try {
          const geo  = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(b.address)}&format=json&limit=1`);
          const geoD = await geo.json();
          if (!geoD[0]) continue;
          const { lat, lon } = geoD[0];
          const wx   = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=weather_code,temperature_2m_max&temperature_unit=fahrenheit&timezone=auto&start_date=${b.date}&end_date=${b.date}`);
          const wxD  = await wx.json();
          if (!cancelled && wxD.daily?.weather_code?.[0] != null) {
            setBookingWeather(prev => ({ ...prev, [b.id]: { icon: wmoIcon(wxD.daily.weather_code[0]), temp: Math.round(wxD.daily.temperature_2m_max[0]) } }));
          }
        } catch { /* non-critical */ }
        await new Promise(r => setTimeout(r, 1100)); // Nominatim: max 1 req/sec
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [isProPlus, bookings]);

  // ── REALTIME ───────────────────────────────────────
  useEffect(() => {
    if (!detailer) return;
    const ch = supabase.channel("dash-bookings")
      .on("postgres_changes", { event:"*", schema:"public", table:"bookings", filter:`detailer_id=eq.${detailer.id}` }, loadAll)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [detailer, loadAll]);

  useEffect(() => {
    if (!selectedAppt) {
      setEditingApptPrice(false);
      setApptPriceEditValue("");
      setAiEstimate(null);
      setEstimatePrice("");
      setEstimateHours("");
    }
  }, [selectedAppt]);

  const generateEstimate = async () => {
    if (!selectedAppt || !detailer) return;
    setGeneratingEstimate(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res   = await fetch("/api/ai-estimate", {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body:    JSON.stringify({ booking_id: selectedAppt.id, detailer_id: detailer.id }),
      });
      const data = await res.json();
      if (res.ok) {
        setAiEstimate(data);
        setEstimatePrice(String(data.price  ?? ""));
        setEstimateHours(String(data.hours  ?? ""));
      } else {
        showToast(data.error || "Could not generate estimate.", "error");
      }
    } catch {
      showToast("Estimate failed. Try again.", "error");
    }
    setGeneratingEstimate(false);
  };

  // ── BOOKING ACTIONS ────────────────────────────────
  const updateStatus = async (id, status) => {
    setUpdating(true);
    const { error } = await supabase.from("bookings").update({ status }).eq("id", id);
    if (error) { showToast("Update failed.", "error"); }
    else {
      showToast(status === "confirmed" ? "Booking confirmed! ✓" : status === "completed" ? "Marked complete! ✓" : status === "cancelled" ? "Booking cancelled." : "Booking declined.");
      // Increment lifetime job count + spend on the customer record when a
      // booking is completed. Guarded so we don't double-count if the booking
      // was already in the completed state.
      if (status === "completed") {
        const appt = bookings.find(b => b.id === id);
        if (appt && appt.status !== "completed" && appt.email) {
          const finalAmount = (appt.amount || 0) + (parseFloat(appt.adjustment_amount) || 0);
          const { data: cust } = await supabase
            .from("customers").select("id, job_count, total_spent")
            .eq("detailer_id", detailer.id).eq("email", appt.email).maybeSingle();
          if (cust) {
            await supabase.from("customers").update({
              job_count:   (cust.job_count   || 0) + 1,
              total_spent: (cust.total_spent || 0) + finalAmount,
            }).eq("id", cust.id);
          }
        }
      }
      // Send status update email to customer for every status change
      const appt = bookings.find(b => b.id === id);
      if (appt?.email) {
        try {
          await fetch("/api/send-email", {
            method:  "POST",
            headers: { "Content-Type": "application/json" },
            body:    JSON.stringify({
              type:       "update",
              booking_id: id,
              status:     status === "confirmed" ? "CONFIRMED ✓" : status === "cancelled" ? "CANCELLED" : "DECLINED",
            }),
          });
        } catch(e) { console.warn("Email failed:", e); }
      }
      setBookings(prev => (status === "declined" || status === "cancelled")
        ? prev.filter(b => b.id !== id)
        : prev.map(b => b.id === id ? { ...b, status } : b));
      setSelectedAppt(null);
    }
    setUpdating(false);
  };

  // ── JOB FLOW ACTIONS ──────────────────────────────
  const confirmArrival = async () => {
    setUpdating(true);
    // SMS (Twilio) — Pro detailers only; sendSms handles the plan check.
    if (activeJob?.phone) {
      await sendSms(
        activeJob.phone,
        `Hi ${activeJob.name.split(" ")[0]}! Your detailer has arrived and is ready to start. — ${detailer.business_name}\nReply STOP to opt out, HELP for help. Msg & data rates may apply.`
      );
    }
    // Email — fires for every account; recipient is sourced from the booking row server-side.
    if (activeJob?.email) {
      try {
        await fetch("/api/send-email", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ type: "arrival", booking_id: activeJob.id }),
        });
      } catch(e) { console.warn("Arrival email failed:", e); }
    }
    setArrivalSent(true);
    setJobStep("before");
    setUpdating(false);
  };


  // Save price adjustment to the booking. Negative = discount, positive = upcharge.
  // Uses activeJob if a job is in progress, otherwise selectedAppt (booking detail modal).
  const _writeAdjustment = async (amt, reason) => {
    const target = activeJob || selectedAppt;
    if (!target) return;
    setSavingAdjustment(true);
    const { error } = await supabase.from("bookings")
      .update({ adjustment_amount: amt, adjustment_reason: reason })
      .eq("id", target.id);
    if (error) {
      showToast("Could not save adjustment.", "error");
    } else {
      if (activeJob)     setActiveJob(prev     => prev ? { ...prev, adjustment_amount: amt, adjustment_reason: reason } : prev);
      if (selectedAppt)  setSelectedAppt(prev  => prev ? { ...prev, adjustment_amount: amt, adjustment_reason: reason } : prev);
      setBookings(prev => prev.map(b => b.id === target.id ? { ...b, adjustment_amount: amt, adjustment_reason: reason } : b));
      showToast(amt === 0 ? "Adjustment cleared." : "Adjustment applied ✓");
    }
    setSavingAdjustment(false);
  };


  const generatePaymentLink = async (totalCents) => {
    setGeneratingPayLink(true); setPayLinkMethod("stripe");
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      const token    = authSess?.access_token;
      const res = await fetch("/api/create-job-payment", {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body:    JSON.stringify({
          detailer_id:    detailer.id,
          booking_id:     activeJob.id,
          amount_cents:   totalCents,
          service_name:   activeJob.service_name,
          customer_name:  activeJob.name,
          customer_email: activeJob.email,
        }),
      });
      const data = await res.json();
      if (data.url) {
        setPaymentUrl(data.url);
        if (activeJob.phone) {
          await sendSms(
            activeJob.phone,
            `Hi ${activeJob.name.split(" ")[0]}! Here's your secure payment link for ${activeJob.service_name}: ${data.url} — ${detailer.business_name}`
          );
        }
      } else {
        showToast(data.error || "Could not generate Stripe payment link.", "error");
      }
    } catch(e) {
      showToast("Payment link failed.", "error");
    }
    setGeneratingPayLink(false);
  };

  const generateSquareLink = async (totalCents) => {
    setGeneratingPayLink(true); setPayLinkMethod("square");
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      const token    = authSess?.access_token;
      const res = await fetch("/api/create-square-checkout", {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body:    JSON.stringify({
          booking_id:    activeJob.id,
          amount_cents:  totalCents,
          service_name:  activeJob.service_name,
          customer_name: activeJob.name,
        }),
      });
      const data = await res.json();
      if (data.url) {
        setPaymentUrl(data.url);
        if (activeJob.phone) {
          await sendSms(
            activeJob.phone,
            `Hi ${activeJob.name.split(" ")[0]}! Here's your secure payment link for ${activeJob.service_name}: ${data.url} — ${detailer.business_name}`
          );
        }
      } else {
        showToast(data.error || "Could not generate Square payment link.", "error");
      }
    } catch(e) {
      showToast("Payment link failed.", "error");
    }
    setGeneratingPayLink(false);
  };

  const closeOutJob = async () => {
    if (activeJob) {
      const completedAt = new Date().toISOString();
      await supabase.from("bookings")
        .update({ completed_at: completedAt })
        .eq("id", activeJob.id);
      setBookings(prev => prev.map(b => b.id === activeJob.id ? { ...b, completed_at: completedAt } : b));
    }
    await updateStatus(activeJob.id, "completed");
    setActiveJob(null);
    setJobStep("arrival");
    setAdjustmentReasonInput("");
    setArrivalSent(false);
    setPaymentUrl(null);
    setPayLinkMethod(null);
    setEditingPrice(false);
    setPriceEditValue("");
  };

  // ── SERVICE ACTIONS ────────────────────────────────
  const openNewService = () => {
    setSvcForm({ name:"", description:"", icon:"✨", duration:"", pricing:{ sedan:"", suv:"", truck:"", van:"" } });
    setSvcModal("new");
  };

  const openEditService = svc => {
    const pricing = { sedan:"", suv:"", truck:"", van:"" };
    svc.service_pricing?.forEach(p => { pricing[p.vehicle_type] = String(p.price); });
    setSvcForm({ name:svc.name, description:svc.description||"", icon:svc.icon||"✨", duration:svc.duration||"", pricing });
    setSvcModal(svc);
  };

  const saveService = async e => {
    e.preventDefault();
    setUpdating(true);
    const isNew = svcModal === "new";
    let svcId;

    if (isNew) {
      const { data, error } = await supabase.from("services").insert([{
        detailer_id: detailer.id, name: svcForm.name, description: svcForm.description,
        icon: svcForm.icon, duration: svcForm.duration, sort_order: services.length,
      }]).select().single();
      if (error) { showToast("Save failed.", "error"); setUpdating(false); return; }
      svcId = data.id;
    } else {
      await supabase.from("services").update({ name:svcForm.name, description:svcForm.description, icon:svcForm.icon, duration:svcForm.duration }).eq("id", svcModal.id);
      svcId = svcModal.id;
      await supabase.from("service_pricing").delete().eq("service_id", svcId);
    }

    const pricingRows = Object.entries(svcForm.pricing)
      .filter(([, v]) => v !== "")
      .map(([vehicle_type, price]) => ({ service_id: svcId, vehicle_type, price: parseFloat(price) }));
    if (pricingRows.length) await supabase.from("service_pricing").insert(pricingRows);

    showToast(isNew ? "Service added! ✓" : "Service updated! ✓");
    setSvcModal(null);
    loadAll();
    setUpdating(false);
  };

  const deleteService = async svcId => {
    if (!confirm("Delete this service?")) return;
    await supabase.from("services").delete().eq("id", svcId);
    showToast("Service deleted.");
    setServices(prev => prev.filter(s => s.id !== svcId));
  };

  // ── INVENTORY (Pro+) ────────────────────────────────
  const openNewInventory = () => {
    setInvForm({ name:"", quantity:"", unit:"each", cost_per_unit:"", low_stock_threshold:"", notes:"" });
    setInvModal("new");
  };
  const openEditInventory = item => {
    setInvForm({
      name: item.name,
      quantity: String(item.quantity ?? ""),
      unit: item.unit || "each",
      cost_per_unit: item.cost_per_unit != null ? String(item.cost_per_unit) : "",
      low_stock_threshold: item.low_stock_threshold != null ? String(item.low_stock_threshold) : "",
      notes: item.notes || "",
    });
    setInvModal(item);
  };
  const saveInventory = async e => {
    e.preventDefault();
    if (!invForm.name.trim()) return;
    setUpdating(true);
    // Empty numeric inputs → null so the optional fields stay un-set
    // rather than persisting as 0 (which would mean "free" or "always low").
    const numOrNull = s => {
      if (s == null || String(s).trim() === "") return null;
      const n = parseFloat(s);
      return isNaN(n) ? null : n;
    };
    const payload = {
      name: invForm.name.trim(),
      quantity: numOrNull(invForm.quantity) ?? 0,
      unit: invForm.unit.trim() || "each",
      cost_per_unit: numOrNull(invForm.cost_per_unit),
      low_stock_threshold: numOrNull(invForm.low_stock_threshold),
      notes: invForm.notes.trim() || null,
    };
    if (invModal === "new") {
      const { data, error } = await supabase.from("inventory_items")
        .insert([{ ...payload, detailer_id: detailer.id }])
        .select().single();
      if (error) { showToast("Save failed.", "error"); setUpdating(false); return; }
      setInventory(prev => [data, ...prev]);
    } else {
      const { data, error } = await supabase.from("inventory_items")
        .update(payload).eq("id", invModal.id).select().single();
      if (error) { showToast("Save failed.", "error"); setUpdating(false); return; }
      setInventory(prev => prev.map(i => i.id === data.id ? data : i));
    }
    setInvModal(null);
    setUpdating(false);
    showToast("Saved ✓");
  };
  const deleteInventory = async id => {
    if (!confirm("Remove this item from inventory?")) return;
    await supabase.from("inventory_items").delete().eq("id", id);
    setInventory(prev => prev.filter(i => i.id !== id));
    showToast("Item removed.");
  };
  const adjustInventoryQty = async (item, delta) => {
    // Optimistic update; integer step matches the +/- buttons. Detailers
    // adjust fractional quantities (e.g. 2.5 gal wax) through the Edit modal.
    const next = Math.max(0, Number((item.quantity || 0) + delta));
    setInventory(prev => prev.map(i => i.id === item.id ? { ...i, quantity: next } : i));
    const { error } = await supabase.from("inventory_items")
      .update({ quantity: next }).eq("id", item.id);
    if (error) {
      // Revert on failure so the UI doesn't drift from the DB.
      setInventory(prev => prev.map(i => i.id === item.id ? { ...i, quantity: item.quantity } : i));
      showToast("Couldn't update quantity.", "error");
    }
  };
  const isLowStock = item =>
    item.low_stock_threshold != null && Number(item.quantity) <= Number(item.low_stock_threshold);
  const inventoryValue = inventory.reduce((sum, i) =>
    sum + (Number(i.quantity) || 0) * (Number(i.cost_per_unit) || 0), 0);

  // ── AI Quotes (Pro+) ────────────────────────────────────
  const savePricingRules = async () => {
    if (!detailer || !pricingRules) return;
    setSavingPricingRules(true);
    const { error } = await supabase
      .from("detailers")
      .update({ quote_pricing_rules: pricingRules })
      .eq("id", detailer.id);
    setSavingPricingRules(false);
    if (error) { showToast("Couldn't save pricing rules.", "error"); return; }
    setDetailer(prev => prev ? { ...prev, quote_pricing_rules: pricingRules } : prev);
    showToast("Pricing rules saved ✓");
  };
  const updateBasePrice = (bodyType, key, value) => {
    setPricingRules(prev => ({
      ...prev,
      base_prices: {
        ...(prev?.base_prices || {}),
        [bodyType]: { ...(prev?.base_prices?.[bodyType] || {}), [key]: value === "" ? "" : Number(value) || 0 },
      },
    }));
  };
  const updateAddOn = (idx, key, value) => {
    setPricingRules(prev => {
      const addOns = Array.isArray(prev?.add_ons) ? [...prev.add_ons] : [];
      addOns[idx] = { ...addOns[idx], [key]: key === "price" ? (value === "" ? "" : Number(value) || 0) : value };
      return { ...prev, add_ons: addOns };
    });
  };
  const addAddOn = () => {
    setPricingRules(prev => ({
      ...prev,
      add_ons: [ ...(Array.isArray(prev?.add_ons) ? prev.add_ons : []), { key: `addon_${Date.now()}`, label: "New add-on", price: 0 } ],
    }));
  };
  const removeAddOn = (idx) => {
    setPricingRules(prev => ({
      ...prev,
      add_ons: (Array.isArray(prev?.add_ons) ? prev.add_ons : []).filter((_, i) => i !== idx),
    }));
  };
  const deleteAiQuote = async (id) => {
    if (!confirm("Delete this quote? Photos will also be removed.")) return;
    const quote = aiQuotes.find(q => q.id === id);
    // Best-effort: delete photo objects from storage. RLS for storage isn't set up
    // for the detailer's anon role; the service-role API would be ideal here but
    // for now we just remove the DB row and orphan the photos (small impact).
    await supabase.from("ai_quotes").delete().eq("id", id);
    setAiQuotes(prev => prev.filter(q => q.id !== id));
    if (expandedQuoteId === id) setExpandedQuoteId(null);
    showToast("Quote deleted.");
    void quote; // referenced for future storage-cleanup wiring
  };
  const lowStockCount = inventory.filter(isLowStock).length;

  // ── AVAILABILITY ───────────────────────────────────
  const MONTH_NAMES_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const MONTH_NAMES_FULL  = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const WEEK_DAYS_SHORT   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const toggleBlockDate = async (year, month, day) => {
    const dateStr = toIsoDate(year, month, day);
    const isBlocked = blockedDates.includes(dateStr);
    if (isBlocked) {
      await supabase.from("blocked_times")
        .delete()
        .eq("detailer_id", detailer.id)
        .eq("date", dateStr);
      setBlockedDates(prev => prev.filter(d => d !== dateStr));
      showToast("Date unblocked ✓");
    } else {
      await supabase.from("blocked_times")
        .insert([{ detailer_id: detailer.id, date: dateStr, time: "all-day" }]);
      setBlockedDates(prev => [...prev, dateStr]);
      showToast("Date blocked ✓");
    }
  };

  const availDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const availFirstDay    = (year, month) => new Date(year, month, 1).getDay();
  const todayDate = new Date(); todayDate.setHours(0,0,0,0);
  const isPastDay = (year, month, day) => new Date(year, month, day) < todayDate;

  const bookingDatesThisMonth = bookings
    .map(b => parseIsoDate(b.date))
    .filter(p => p && p.year === availCalYear && p.month === availCalMonth)
    .map(p => p.day);

  // ── PHOTOS ────────────────────────────────────────
  const uploadPhoto = async (file, bookingId, type) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const ext  = file.name.split(".").pop();
      const path = `${detailer.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("job-photos")
        .upload(path, file, { contentType: file.type });
      if (upErr) { showToast("Upload failed.", "error"); setUploadingPhoto(false); return; }

      const { data: { publicUrl } } = supabase.storage
        .from("job-photos")
        .getPublicUrl(path);

      const { data: photo } = await supabase.from("job_photos").insert([{
        detailer_id: detailer.id,
        booking_id:  bookingId || null,
        url:         publicUrl,
        path,
        type,  // "before" | "after" | "general"
      }]).select().single();

      if (photo) setPhotos(prev => [photo, ...prev]);
      showToast("Photo uploaded! ✓");
    } catch(e) {
      showToast("Upload failed.", "error");
    }
    setUploadingPhoto(false);
  };

  const deletePhoto = async (photo) => {
    await supabase.storage.from("job-photos").remove([photo.path]);
    await supabase.from("job_photos").delete().eq("id", photo.id);
    setPhotos(prev => prev.filter(p => p.id !== photo.id));
    showToast("Photo deleted.");
  };

  // ── SMS ───────────────────────────────────────────
  const sendSms = async (phone, message) => {
    if (!phone || !message) return;
    setSendingSms(true);
    try {
      const res = await fetch("/api/send-sms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: phone, message, detailer_id: detailer.id }),
      });
      const data = await res.json();
      if (data.success) {
        showToast("SMS sent! ✓");
        setSmsForm({ phone:"", message:"" });
      } else {
        showToast(data.error || "SMS failed.", "error");
      }
    } catch(e) {
      showToast("SMS failed.", "error");
    }
    setSendingSms(false);
  };

  // ── SETTINGS ───────────────────────────────────────
  const [settingsForm, setSettingsForm] = useState(null);
  useEffect(() => { if (detailer) setSettingsForm({ business_name: detailer.business_name, phone: detailer.phone||"", city: detailer.city||"", state: detailer.state||"", bio: detailer.bio||"", require_deposit: detailer.require_deposit||false, service_address: detailer.service_address||"", service_radius: detailer.service_radius||25, service_lat: detailer.service_lat ?? null, service_lng: detailer.service_lng ?? null, work_days: detailer.work_days || "mon,tue,wed,thu,fri,sat", work_start: detailer.work_start ?? 8, work_end: detailer.work_end ?? 17, square_enabled: detailer.square_enabled||false, square_access_token: detailer.square_access_token||"" }); }, [detailer]);

  // ── Service area map refs ──────────────────────────────────────────
  const mapDivRef  = useRef(null);
  const mapObjRef  = useRef(null);
  const circleRef  = useRef(null);

  // Initialize / re-center map when address changes
  useEffect(() => {
    if (tab !== "settings" || !isPro || !GOOGLE_MAPS_KEY || !settingsForm?.service_address) return;
    const loadMap = async () => {
      if (!window.google?.maps) {
        await new Promise((resolve, reject) => {
          if (document.getElementById("gmap-script")) { resolve(); return; }
          const s = document.createElement("script");
          s.id = "gmap-script";
          s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}`;
          s.onload = resolve; s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      if (!mapDivRef.current) return;
      const geo = new window.google.maps.Geocoder();
      geo.geocode({ address: settingsForm.service_address }, (results, status) => {
        if (status !== "OK" || !results[0] || !mapDivRef.current) return;
        const center = results[0].geometry.location;
        // Capture lat/lng into the form so the next save persists them.
        // Booking pages need these to compute "outside service area" warnings.
        const lat = center.lat(), lng = center.lng();
        setSettingsForm(f => (f && (f.service_lat !== lat || f.service_lng !== lng))
          ? { ...f, service_lat: lat, service_lng: lng }
          : f);
        const radiusM = (settingsForm.service_radius || 25) * 1609.34;
        if (!mapObjRef.current) {
          mapObjRef.current = new window.google.maps.Map(mapDivRef.current, {
            center, zoom: 10, disableDefaultUI: true, zoomControl: true,
          });
        } else {
          mapObjRef.current.setCenter(center);
        }
        if (circleRef.current) circleRef.current.setMap(null);
        circleRef.current = new window.google.maps.Circle({
          map: mapObjRef.current, center, radius: radiusM,
          strokeColor: "#3b82f6", strokeOpacity: 0.9, strokeWeight: 2,
          fillColor: "#3b82f6", fillOpacity: 0.15,
        });
        mapObjRef.current.fitBounds(circleRef.current.getBounds());
      });
    };
    loadMap();
  }, [tab, settingsForm?.service_address, isPro]);

  // Update circle radius live when slider moves
  useEffect(() => {
    if (!circleRef.current || !mapObjRef.current) return;
    const r = (settingsForm?.service_radius || 25) * 1609.34;
    circleRef.current.setRadius(r);
    mapObjRef.current.fitBounds(circleRef.current.getBounds());
  }, [settingsForm?.service_radius]);

  const saveSettings = async e => {
    e.preventDefault();
    await supabase.from("detailers").update(settingsForm).eq("id", detailer.id);
    setDetailer(d => ({ ...d, ...settingsForm }));
    showToast("Settings saved! ✓");
  };

  // ── UPGRADE / BILLING ─────────────────────────────
  const handleUpgrade = async (priceId) => {
    if (!priceId) { showToast("Pricing not configured yet.", "error"); return; }
    const res  = await fetch("/api/stripe-billing", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ action: "checkout", detailer_id: detailer.id, price_id: priceId, customer_email: session.user.email, stripe_customer_id: detailer.stripe_customer_id || null }),
    });
    const data = await res.json();
    if (data.url) window.location.href = data.url;
    else showToast("Could not start checkout. Try again.", "error");
  };

  const handleUnlinkStripe = async () => {
    if (!confirm("Unlink your Stripe account? This will also disable the $50 deposit requirement.")) return;
    const { data: { session: authSess } } = await supabase.auth.getSession();
    const token = authSess?.access_token;
    const res  = await fetch("/api/stripe-account", {
      method:  "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body:    JSON.stringify({ detailer_id: detailer.id }),
    });
    const data = await res.json();
    if (data.success) {
      showToast("Stripe account unlinked.");
      loadAll();
    } else {
      showToast("Could not unlink Stripe. Try again.", "error");
    }
  };

  const handleManageSubscription = async () => {
    const { data: { session: authSess } } = await supabase.auth.getSession();
    const token = authSess?.access_token;
    const res  = await fetch("/api/stripe-billing", {
      method:  "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body:    JSON.stringify({ action: "portal", stripe_customer_id: detailer.stripe_customer_id }),
    });
    const data = await res.json();
    if (data.url) window.location.href = data.url;
    else showToast("Could not open billing portal. Try again.", "error");
  };

  // Handle ?upgraded=1 redirect from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "1") {
      showToast("🎉 Welcome to Pro! All features are now unlocked.");
      window.history.replaceState({}, "", "/dashboard");
      loadAll();
    }
  }, []); // eslint-disable-line

  // ── SIGN OUT ───────────────────────────────────────
  const signOut = async () => { await supabase.auth.signOut(); navigate("/"); };

  // ── STATS ──────────────────────────────────────────
  // Revenue excludes declined and cancelled bookings (they were never earned)
  // and matches what the "Earnings by Service" panel below already does.
  const revenue   = bookings
    .filter(b => b.status !== "declined" && b.status !== "cancelled")
    .reduce((a, b) => a + (b.amount || 0) + (parseFloat(b.adjustment_amount) || 0), 0);
  const confirmed = bookings.filter(b => b.status === "confirmed").length;
  const pending   = bookings.filter(b => b.status === "pending").length;

  // ── REVENUE TAB: completed-only totals keyed off completed_at ─────
  // Period boundaries are local-time: today starts at 00:00, the week starts
  // Sunday 00:00, the month on the 1st 00:00, the year on Jan 1 00:00.
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfWeek  = new Date(startOfToday); startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfYear  = new Date(now.getFullYear(), 0, 1);
  const bookingFinal = b => (b.amount || 0) + (parseFloat(b.adjustment_amount) || 0);
  const completedJobs = bookings.filter(b => b.status === "completed" && b.completed_at);
  const sumSince = since => completedJobs
    .filter(b => new Date(b.completed_at) >= since)
    .reduce((a, b) => a + bookingFinal(b), 0);
  const todayRev = sumSince(startOfToday);
  const weekRev  = sumSince(startOfWeek);
  const monthRev = sumSince(startOfMonth);
  const yearRev  = sumSince(startOfYear);

  // Earnings by service for completed jobs in the current month.
  const monthByService = {};
  completedJobs.filter(b => new Date(b.completed_at) >= startOfMonth).forEach(b => {
    monthByService[b.service_name] = (monthByService[b.service_name] || 0) + bookingFinal(b);
  });
  const monthServiceEntries = Object.entries(monthByService).sort((a,b) => b[1]-a[1]);
  const monthServiceMax = monthServiceEntries[0]?.[1] || 1;

  const fmtMoney = n => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  const weeklyGoal  = parseFloat(detailer?.revenue_goal_weekly)  || 0;
  const monthlyGoal = parseFloat(detailer?.revenue_goal_monthly) || 0;

  const openGoalEditor = () => {
    setGoalWeeklyInput(weeklyGoal ? String(weeklyGoal) : "");
    setGoalMonthlyInput(monthlyGoal ? String(monthlyGoal) : "");
    setEditingGoals(true);
  };

  const saveGoals = async () => {
    setSavingGoals(true);
    // Empty input → null (no goal); any other value → number (NaN guard).
    const parseGoal = s => {
      if (s.trim() === "") return null;
      const n = parseFloat(s);
      return isNaN(n) || n < 0 ? null : n;
    };
    const next = {
      revenue_goal_weekly:  parseGoal(goalWeeklyInput),
      revenue_goal_monthly: parseGoal(goalMonthlyInput),
    };
    const { error } = await supabase.from("detailers").update(next).eq("id", detailer.id);
    if (error) {
      showToast("Couldn't save goals — try again.", "error");
    } else {
      setDetailer(prev => ({ ...prev, ...next }));
      setEditingGoals(false);
      showToast("Goals saved ✓");
    }
    setSavingGoals(false);
  };
  const bookingUrl = `${window.location.origin}/book/${detailer?.slug || ""}`;

  if (loadingData && !detailer && !needsOnboarding) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh" }}>
      <span className="spinner" style={{ width:32, height:32, border:"3px solid rgba(59,130,246,.2)", borderTopColor:"var(--blue)" }} />
    </div>
  );

  // New user — no detailer profile yet, complete setup inline
  if (needsOnboarding) return (
    <>
      <style>{css}</style>
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
        <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:20, padding:40, maxWidth:480, width:"100%" }} className="fade-in">
          <div style={{ fontFamily:"var(--head)", fontSize:22, fontWeight:800, letterSpacing:2, marginBottom:24 }}>
            NEO<span style={{ color:"var(--blue)" }}> DETAIL</span>
          </div>
          <div style={{ fontFamily:"var(--head)", fontSize:22, fontWeight:800, letterSpacing:1, marginBottom:6 }}>COMPLETE YOUR SETUP</div>
          <div style={{ color:"var(--muted)", fontSize:14, marginBottom:28 }}>Tell us about your business to activate your booking page.</div>
          <form onSubmit={finishOnboarding}>
            <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:24 }}>
              <div className="form-group">
                <label>Business Name</label>
                <input type="text" placeholder="e.g. Jay's Auto Detailing"
                  value={onboardForm.bizName}
                  onChange={e => {
                    const val = e.target.value;
                    setOnboardForm(f => ({ ...f, bizName:val }));
                    checkOnboardSlug(val);
                  }} required />
              </div>
              <div className="form-group">
                <label>Your Booking URL</label>
                <input type="text" placeholder="jays-auto-detailing"
                  value={onboardForm.slug}
                  onChange={e => checkOnboardSlug(e.target.value)} required />
                <div style={{ marginTop:6, fontSize:12, color:"var(--muted)" }}>
                  neodetail.app/book/<span style={{ color:"var(--teal)", fontWeight:500 }}>{onboardForm.slug || "your-name"}</span>
                </div>
                {slugStatus === "ok"       && <div style={{ color:"var(--success)", fontSize:12, marginTop:4 }}>✓ Available</div>}
                {slugStatus === "taken"    && <div style={{ color:"var(--danger)",  fontSize:12, marginTop:4 }}>✕ Already taken — try another</div>}
                {slugStatus === "checking" && <div style={{ color:"var(--muted)",   fontSize:12, marginTop:4 }}>Checking...</div>}
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" placeholder="(555) 000-0000"
                  value={onboardForm.phone}
                  onChange={e => setOnboardForm(f => ({ ...f, phone:e.target.value }))} />
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div className="form-group">
                  <label>City</label>
                  <input type="text" placeholder="Akron"
                    value={onboardForm.city}
                    onChange={e => setOnboardForm(f => ({ ...f, city:e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input type="text" placeholder="OH" maxLength={2}
                    value={onboardForm.state}
                    onChange={e => setOnboardForm(f => ({ ...f, state:e.target.value.toUpperCase() }))} />
                </div>
              </div>
            </div>
            {onboardError && <div style={{ color:"var(--danger)", fontSize:13, marginBottom:12, textAlign:"center" }}>{onboardError}</div>}
            <button className="btn-primary full" type="submit"
              disabled={onboardLoading || slugStatus === "taken" || !onboardForm.bizName || !onboardForm.slug}>
              {onboardLoading ? <><span className="spinner"/>Setting up your page...</> : "Launch My Page →"}
            </button>
          </form>
          <div style={{ textAlign:"center", marginTop:16 }}>
            <button className="btn-ghost" style={{ fontSize:13 }} onClick={() => supabase.auth.signOut().then(() => navigate("/"))}>Sign Out</button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      <div className="dash-shell">
        {/* NAV */}
        <nav className="dash-nav">
          <div className="dash-nav-logo">NEO<span> DETAIL</span></div>
          <div className="dash-nav-tabs">
            {[["overview","📊 Overview"],["customers","👥 Customers"],["revenue","💰 Revenue"],["availability","📅 Availability"],["services","🛠 Services"], ...(isProPlus ? [["inventory","📦 Inventory"],["aiquotes","✨ AI Quotes"]] : []), ["settings","⚙️ Settings"]].map(([id, label]) => (
              <button key={id} className={`dash-tab ${tab === id ? "active" : ""}`} onClick={() => setTab(id)}>
                {label}
              </button>
            ))}
          </div>
          <div className="dash-nav-right">
            <span className={`badge ${isPro ? "pro" : "free"}`}>{isPro ? "PRO" : "FREE"}</span>
            {session?.user?.id === ADMIN_ID && (
              <button className="btn-edit" style={{ fontSize:12 }} onClick={() => navigate("/admin")}>⚙ Admin</button>
            )}
            <button className="btn-ghost" onClick={signOut}>Sign Out</button>
          </div>
        </nav>

        {/* BOTTOM NAV (mobile) */}
        <nav className="bottom-nav">
          {[["overview","📊","HOME"],["customers","👥","CRM"],["revenue","💰","REV"],["availability","📅","AVAIL"],["services","🛠","SVCS"], ...(isProPlus ? [["inventory","📦","INV"],["aiquotes","✨","QUOTES"]] : []), ["settings","⚙️","SETTINGS"]].map(([id,icon,label]) => (
            <button key={id} className={`bottom-nav-item ${tab === id ? "active" : ""}`} onClick={() => setTab(id)}>
              <span className="bottom-nav-icon">{icon}</span>
              {label}
            </button>
          ))}
        </nav>

        <div className="dash-content fade-in">

          {/* ── PAST-DUE BILLING BANNER ───────────────── */}
          {detailer?.subscription_status === "past_due" && (
            <div style={{ background:"rgba(245,158,11,.12)", border:"1px solid #f59e0b", borderRadius:12, padding:"12px 18px", marginBottom:20, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:20 }}>⚠️</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:"#f59e0b" }}>Payment Past Due</div>
                  <div style={{ fontSize:13, color:"var(--muted)" }}>Your Pro subscription payment failed. Update your card to keep Pro access.</div>
                </div>
              </div>
              <button className="btn-primary" style={{ fontSize:13, padding:"8px 16px", background:"#f59e0b", flexShrink:0 }} onClick={handleManageSubscription}>
                Update Card →
              </button>
            </div>
          )}

          {/* ── OVERVIEW ─────────────────────────────── */}
          {tab === "overview" && (
            <>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
                <div>
                  <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2 }}>DASHBOARD</h2>
                  <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>{detailer?.business_name}</div>
                </div>
                <div className="link-box mobile-hide" style={{ maxWidth:380 }}>
                  <span>{bookingUrl}</span>
                  <button className="btn-edit" onClick={() => { navigator.clipboard.writeText(bookingUrl); showToast("Link copied! ✓"); }}>Copy</button>
                  <a href={bookingUrl} target="_blank" rel="noreferrer"><button className="btn-edit">View →</button></a>
                </div>
              </div>

              {/* ── WEATHER (Pro) ──────────────────────── */}
              {isPro ? (
                <div className="panel" style={{ padding:18, marginBottom:20 }}>
                  {weatherLoading && !weather ? (
                    <div style={{ display:"flex", alignItems:"center", gap:12, color:"var(--muted)", fontSize:14 }}>
                      <span className="spinner" style={{ width:16, height:16, border:"2px solid rgba(59,130,246,.2)", borderTopColor:"var(--blue)" }}/>
                      Loading local weather…
                    </div>
                  ) : !weather ? (
                    <div style={{ color:"var(--muted)", fontSize:14, display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:22 }}>🗺️</span>
                      <span>Add a city or service area in <button className="btn-edit" style={{ display:"inline", fontSize:13, padding:"3px 10px" }} onClick={() => setTab("settings")}>Settings</button> to see local weather.</span>
                    </div>
                  ) : (() => {
                    const cur     = weather.current;
                    const daily   = weather.daily;
                    const today   = wxFromCode(cur.weather_code);
                    const todayPrecip = daily.precipitation_probability_max?.[0] ?? 0;
                    const todayHigh   = Math.round(daily.temperature_2m_max[0]);
                    const todayLow    = Math.round(daily.temperature_2m_min[0]);
                    const goodForDetailing =
                      !isPrecipCode(cur.weather_code) &&
                      !isPrecipCode(daily.weather_code[0]) &&
                      todayPrecip < 40 &&
                      todayHigh >= 40 && todayHigh <= 100;
                    return (
                      <>
                        <div style={{ display:"flex", alignItems:"center", gap:18, marginBottom:14, flexWrap:"wrap" }}>
                          <div style={{ fontSize:54, lineHeight:1 }}>{today.icon}</div>
                          <div style={{ flex:1, minWidth:140 }}>
                            <div style={{ fontFamily:"var(--head)", fontSize:40, fontWeight:800, lineHeight:1 }}>
                              {Math.round(cur.temperature_2m)}°F
                            </div>
                            <div style={{ color:"var(--muted)", fontSize:13, marginTop:6 }}>
                              {today.label} · {weather.locationLabel}
                            </div>
                            <div style={{ color:"var(--muted)", fontSize:12, marginTop:3 }}>
                              High {todayHigh}° · Low {todayLow}° · {todayPrecip}% chance of rain
                            </div>
                          </div>
                          <div style={{
                            background: goodForDetailing ? "rgba(34,197,94,.1)" : "rgba(245,158,11,.1)",
                            border:    `1px solid ${goodForDetailing ? "rgba(34,197,94,.3)" : "rgba(245,158,11,.3)"}`,
                            color:      goodForDetailing ? "var(--success)" : "var(--warn)",
                            borderRadius:10, padding:"10px 14px", fontSize:13, fontWeight:600, whiteSpace:"nowrap",
                          }}>
                            {goodForDetailing ? "✓ Good detailing weather" : "⚠ Plan around the weather"}
                          </div>
                        </div>

                        <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:8, borderTop:"1px solid var(--border)", paddingTop:14 }}>
                          {[1,2,3,4,5].map(i => {
                            if (!daily.time?.[i]) return null;
                            const d        = new Date(daily.time[i]);
                            const dayName  = d.toLocaleDateString("en-US", { weekday: "short" });
                            const { icon } = wxFromCode(daily.weather_code[i]);
                            const hi       = Math.round(daily.temperature_2m_max[i]);
                            const lo       = Math.round(daily.temperature_2m_min[i]);
                            const precip   = daily.precipitation_probability_max?.[i] ?? 0;
                            return (
                              <div key={i} style={{ background:"var(--card2)", border:"1px solid var(--border)", borderRadius:10, padding:"10px 6px", textAlign:"center" }}>
                                <div style={{ fontSize:11, color:"var(--muted)", fontWeight:700, letterSpacing:1, marginBottom:4 }}>{dayName.toUpperCase()}</div>
                                <div style={{ fontSize:22, lineHeight:1, marginBottom:6 }}>{icon}</div>
                                <div style={{ fontSize:12, fontWeight:600 }}>{hi}° / {lo}°</div>
                                <div style={{ fontSize:10, color: precip >= 40 ? "var(--warn)" : "var(--muted)", marginTop:2 }}>
                                  {precip}% rain
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div style={{ background:"rgba(59,130,246,.06)", border:"1px dashed rgba(59,130,246,.3)", borderRadius:14, padding:14, marginBottom:20, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                  <div style={{ fontSize:26 }}>🌤️</div>
                  <div style={{ flex:1, minWidth:200 }}>
                    <div style={{ fontWeight:600, fontSize:14 }}>
                      Local Weather <span style={{ fontSize:11, background:"rgba(59,130,246,.15)", color:"var(--blue)", borderRadius:4, padding:"2px 7px", marginLeft:6 }}>PRO</span>
                    </div>
                    <div style={{ color:"var(--muted)", fontSize:13, marginTop:2 }}>Today's forecast + 5-day outlook so you can plan around the weather.</div>
                  </div>
                  <button className="btn-primary" style={{ fontSize:13 }} onClick={() => setTab("settings")}>Upgrade →</button>
                </div>
              )}

              <div className="stats-grid">
                <div className="stat-card"><div className="stat-label">Revenue</div><div className="stat-val teal">{fmt$(revenue)}</div></div>
                <div className="stat-card"><div className="stat-label">Total Bookings</div><div className="stat-val blue">{bookings.length}</div></div>
                <div className="stat-card"><div className="stat-label">Confirmed</div><div className="stat-val">{confirmed}</div></div>
                <div className="stat-card"><div className="stat-label">Pending</div><div className="stat-val warn">{pending}</div></div>
                <div className="stat-card"><div className="stat-label">Customers</div><div className="stat-val blue">{customers.length}</div></div>
              </div>

              <div className="panel">
                <div className="panel-header">
                  <h3>ALL BOOKINGS</h3>
                  <span style={{ fontSize:12, color:"var(--muted)" }}>{bookings.length} total</span>
                </div>
                {loadingData ? (
                  <div className="empty-state"><span className="spinner" style={{ borderTopColor:"var(--blue)" }} /></div>
                ) : bookings.length === 0 ? (
                  <div className="empty-state"><div className="e-icon">📅</div><p>No bookings yet.<br />Share your booking link to get started!</p></div>
                ) : bookings.map(b => (
                  <div className="appt-row" key={b.id} onClick={() => {
                    setSelectedAppt(b);
                  }}>
                    <div className="appt-avatar">{initials(b.name)}</div>
                    <div className="appt-info">
                      <div className="appt-name">{b.name}</div>
                      <div className="appt-sub">{b.service_name} · {b.vehicle_label}</div>
                      <div style={{ marginTop:4 }}><span className={`badge ${b.status}`}>{b.status}</span></div>
                    </div>
                    <div className="appt-right">
                      <div className="appt-amount">{fmt$(b.amount)}</div>
                      <div className="appt-time">{formatDate(b.date)} · {b.time}</div>
                      {isProPlus && bookingWeather[b.id] && (
                        <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>
                          {bookingWeather[b.id].icon} {bookingWeather[b.id].temp}°F
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Earnings by service */}
              {bookings.length > 0 && (() => {
                const byService = {};
                bookings.filter(b => b.status !== "declined" && b.status !== "cancelled").forEach(b => {
                  byService[b.service_name] = (byService[b.service_name] || 0) + (b.amount || 0);
                });
                const entries = Object.entries(byService).sort((a,b) => b[1]-a[1]);
                const max = entries[0]?.[1] || 1;
                return (
                  <div className="panel" style={{ marginTop:20 }}>
                    <div className="panel-header"><h3>EARNINGS BY SERVICE</h3></div>
                    <div style={{ padding:"16px 20px", display:"flex", flexDirection:"column", gap:12 }}>
                      {entries.map(([name, total]) => (
                        <div key={name}>
                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
                            <span>{name}</span>
                            <span style={{ fontFamily:"var(--head)", color:"var(--teal)", fontWeight:700 }}>{fmt$(total)}</span>
                          </div>
                          <div className="earnings-bar-wrap">
                            <div className="earnings-bar" style={{ width:`${(total/max)*100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {/* ── CUSTOMERS (CRM) ──────────────────────── */}
          {tab === "customers" && (
            <>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
                <div>
                  <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2 }}>CUSTOMERS</h2>
                  <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>{customers.length} total customers</div>
                </div>
              </div>

              {selectedCustomer ? (
                <div className="panel fade-in">
                  <div className="panel-header">
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <button className="btn-edit" onClick={() => setSelectedCustomer(null)}>← Back</button>
                      <h3>{selectedCustomer.name.toUpperCase()}</h3>
                    </div>
                    <span className="cust-spent">{fmt$(selectedCustomer.total_spent || 0)}</span>
                  </div>
                  <div style={{ padding:20, display:"flex", flexDirection:"column", gap:14 }}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      {[["Email", selectedCustomer.email], ["Phone", selectedCustomer.phone||"—"], ["Total Spent", fmt$(selectedCustomer.total_spent||0)], ["Jobs", selectedCustomer.job_count||0]].map(([k,v]) => (
                        <div key={k} style={{ background:"var(--card2)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 14px" }}>
                          <div style={{ fontSize:11, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>{k}</div>
                          <div style={{ fontSize:15, fontWeight:600 }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {selectedCustomer.notes && (
                      <div style={{ background:"var(--card2)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 14px" }}>
                        <div style={{ fontSize:11, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Notes</div>
                        <div style={{ fontSize:14 }}>{selectedCustomer.notes}</div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Booking History</div>
                      {bookings.filter(b => b.customer_id === selectedCustomer.id || b.email === selectedCustomer.email).map(b => (
                        <div key={b.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid var(--border)", fontSize:14 }}>
                          <div>
                            <div style={{ fontWeight:600 }}>{b.service_name}</div>
                            <div style={{ color:"var(--muted)", fontSize:12 }}>{formatDate(b.date)} · {b.time}</div>
                          </div>
                          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                            <span style={{ fontFamily:"var(--head)", color:"var(--teal)", fontWeight:700 }}>{fmt$(b.amount)}</span>
                            <span className={`badge ${b.status}`}>{b.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="panel">
                  <div className="panel-header"><h3>ALL CUSTOMERS</h3><span style={{ fontSize:12, color:"var(--muted)" }}>{customers.length} total</span></div>
                  {customers.length === 0 ? (
                    <div className="empty-state"><div className="e-icon">👥</div><p>No customers yet. They appear automatically when someone books.</p></div>
                  ) : customers.map(c => (
                    <div className="customer-row" key={c.id} onClick={() => setSelectedCustomer(c)}>
                      <div className="appt-avatar">{initials(c.name)}</div>
                      <div className="cust-info">
                        <div className="cust-name">{c.name}</div>
                        <div className="cust-sub">{c.email} · {c.phone||"—"}</div>
                      </div>
                      <div className="cust-right">
                        <div className="cust-spent">{fmt$(c.total_spent||0)}</div>
                        <div className="cust-jobs">{c.job_count||0} job{c.job_count !== 1 ? "s" : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── PHOTOS ────────────────────────────── */}
              <div style={{ marginTop:24 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:12 }}>
                  <div>
                    <h3 style={{ fontFamily:"var(--head)", fontSize:20, fontWeight:800, letterSpacing:1 }}>PHOTOS</h3>
                    <div style={{ color:"var(--muted)", fontSize:13, marginTop:2 }}>{photos.length} photos · Before/after shots from your jobs</div>
                  </div>
                  <label style={{ cursor:"pointer" }}>
                    <input type="file" accept="image/*" multiple style={{ display:"none" }}
                      onChange={async e => {
                        for (const file of Array.from(e.target.files)) {
                          await uploadPhoto(file, null, "general");
                        }
                      }} />
                    <span className="btn-primary" style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"9px 16px", fontSize:13, borderRadius:10, cursor:"pointer" }}>
                      {uploadingPhoto ? <><span className="spinner"/>Uploading...</> : "📷 Upload Photos"}
                    </span>
                  </label>
                </div>

                <div className="panel">
                  <div className="panel-header">
                    <h3>ALL PHOTOS</h3>
                    <span style={{ fontSize:12, color:"var(--muted)" }}>{photos.length} total</span>
                  </div>
                  <div style={{ padding:20 }}>
                    {photos.length === 0 ? (
                      <div className="empty-state">
                        <div className="e-icon">📷</div>
                        <p>No photos yet.<br/>Upload before/after shots from your jobs to build your portfolio.</p>
                      </div>
                    ) : (
                      <div className="photo-grid">
                        {photos.map(photo => (
                          <div className="photo-thumb" key={photo.id}>
                            <img src={photo.url} alt={photo.type} loading="lazy" />
                            <div className="photo-type-badge">{photo.type}</div>
                            <button className="photo-del" onClick={() => deletePhoto(photo)}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* SMS panel */}
                {isPro ? (
                  <div className="panel" style={{ marginTop:20 }}>
                    <div className="panel-header"><h3>SEND SMS</h3><span style={{ fontSize:12, color:"var(--muted)" }}>Pro · Twilio</span></div>
                    <div style={{ padding:20, display:"flex", flexDirection:"column", gap:14 }}>
                      <div style={{ fontSize:13, color:"var(--muted)" }}>Send a text message directly to any customer. Great for reminders, follow-ups, and confirmations.</div>
                      <div className="form-group">
                        <label>Phone Number</label>
                        <input type="tel" placeholder="+1 (555) 000-0000" value={smsForm.phone}
                          onChange={e => setSmsForm(f => ({ ...f, phone:e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label>Message</label>
                        <textarea placeholder="Hi! Just confirming your detail appointment tomorrow at 10am. See you then! — NEO Mobile Detailing"
                          value={smsForm.message}
                          onChange={e => setSmsForm(f => ({ ...f, message:e.target.value }))}
                          style={{ minHeight:80 }} />
                        <span style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>{smsForm.message.length}/160 chars</span>
                      </div>
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                        {[
                          "Confirming your detail appt tomorrow — see you then!",
                          "Your detail is complete! Thanks for choosing us 🚗✨",
                          "Reminder: your appointment is in 2 hours.",
                        ].map(t => (
                          <button key={t} className="btn-edit" style={{ fontSize:11, padding:"5px 10px" }}
                            onClick={() => setSmsForm(f => ({ ...f, message:`${t} — ${detailer.business_name}` }))}>
                            {t.slice(0,30)}...
                          </button>
                        ))}
                      </div>
                      <button className="btn-primary"
                        disabled={!smsForm.phone || !smsForm.message || sendingSms}
                        onClick={() => sendSms(smsForm.phone, smsForm.message)}>
                        {sendingSms ? <><span className="spinner"/>Sending...</> : "Send SMS →"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="panel" style={{ marginTop:20 }}>
                    <div className="panel-header"><h3>SMS NOTIFICATIONS</h3></div>
                    <div style={{ padding:20 }}>
                      <div style={{ background:"rgba(59,130,246,.06)", border:"1px dashed rgba(59,130,246,.3)", borderRadius:12, padding:20, textAlign:"center" }}>
                        <div style={{ fontSize:28, marginBottom:10 }}>💬</div>
                        <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>SMS Reminders <span style={{ fontSize:11, background:"rgba(59,130,246,.15)", color:"var(--blue)", borderRadius:4, padding:"2px 7px", marginLeft:4 }}>PRO</span></div>
                        <div style={{ color:"var(--muted)", fontSize:13, marginBottom:14 }}>Send text reminders and confirmations directly to customers. Reduce no-shows by 80%.</div>
                        <button className="btn-primary" style={{ fontSize:13 }} onClick={() => setTab("settings")}>Upgrade to Pro →</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── REVENUE ──────────────────────────────── */}
          {tab === "revenue" && (
            <>
              <div style={{ marginBottom:24 }}>
                <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2 }}>REVENUE</h2>
                <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>Completed jobs only · resets at the start of each period</div>
              </div>

              <div className="stats-grid">
                <div className="stat-card"><div className="stat-label">Today</div><div className="stat-val teal">{fmtMoney(todayRev)}</div></div>
                <div className="stat-card"><div className="stat-label">This Week</div><div className="stat-val teal">{fmtMoney(weekRev)}</div></div>
                <div className="stat-card"><div className="stat-label">This Month</div><div className="stat-val teal">{fmtMoney(monthRev)}</div></div>
                <div className="stat-card"><div className="stat-label">This Year</div><div className="stat-val teal">{fmtMoney(yearRev)}</div></div>
              </div>

              {/* Goals */}
              <div className="panel" style={{ marginTop:20 }}>
                <div className="panel-header">
                  <h3>GOALS</h3>
                  {!editingGoals && (
                    <button className="goal-edit" onClick={openGoalEditor}>
                      {weeklyGoal || monthlyGoal ? "Edit" : "Set goals"}
                    </button>
                  )}
                </div>
                <div style={{ padding:"16px 20px" }}>
                  {editingGoals ? (
                    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                        <label style={{ fontSize:13, fontWeight:600 }}>Weekly goal</label>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ color:"var(--muted)", fontSize:14 }}>$</span>
                          <input className="goal-input" type="number" min="0" step="1" placeholder="0"
                            value={goalWeeklyInput} onChange={e => setGoalWeeklyInput(e.target.value)} />
                        </div>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                        <label style={{ fontSize:13, fontWeight:600 }}>Monthly goal</label>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ color:"var(--muted)", fontSize:14 }}>$</span>
                          <input className="goal-input" type="number" min="0" step="1" placeholder="0"
                            value={goalMonthlyInput} onChange={e => setGoalMonthlyInput(e.target.value)} />
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:4 }}>
                        <button className="goal-edit" onClick={() => setEditingGoals(false)} disabled={savingGoals}>Cancel</button>
                        <button className="btn-primary" style={{ padding:"6px 16px", fontSize:13 }} onClick={saveGoals} disabled={savingGoals}>
                          {savingGoals ? <span className="spinner"/> : "Save"}
                        </button>
                      </div>
                      <div style={{ fontSize:11, color:"var(--muted)" }}>Leave blank to clear a goal.</div>
                    </div>
                  ) : (!weeklyGoal && !monthlyGoal) ? (
                    <div style={{ color:"var(--muted)", fontSize:13 }}>No goals set yet — tap <em>Set goals</em> to add a weekly or monthly target.</div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
                      {weeklyGoal > 0 && (() => {
                        const pct = Math.min(100, (weekRev / weeklyGoal) * 100);
                        const hit = weekRev >= weeklyGoal;
                        return (
                          <div>
                            <div className="goal-row">
                              <span style={{ fontSize:13, fontWeight:600 }}>Weekly {hit && <span style={{ color:"var(--teal)" }}>✓</span>}</span>
                              <span style={{ fontFamily:"var(--head)", fontSize:14, fontWeight:700 }}>
                                <span style={{ color:"var(--teal)" }}>{fmtMoney(weekRev)}</span>
                                <span style={{ color:"var(--muted)" }}> / {fmtMoney(weeklyGoal)}</span>
                              </span>
                            </div>
                            <div className="goal-track"><div className="goal-fill" style={{ width:`${pct}%` }} /></div>
                            <div style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>{pct.toFixed(0)}% of weekly target</div>
                          </div>
                        );
                      })()}
                      {monthlyGoal > 0 && (() => {
                        const pct = Math.min(100, (monthRev / monthlyGoal) * 100);
                        const hit = monthRev >= monthlyGoal;
                        return (
                          <div>
                            <div className="goal-row">
                              <span style={{ fontSize:13, fontWeight:600 }}>Monthly {hit && <span style={{ color:"var(--teal)" }}>✓</span>}</span>
                              <span style={{ fontFamily:"var(--head)", fontSize:14, fontWeight:700 }}>
                                <span style={{ color:"var(--teal)" }}>{fmtMoney(monthRev)}</span>
                                <span style={{ color:"var(--muted)" }}> / {fmtMoney(monthlyGoal)}</span>
                              </span>
                            </div>
                            <div className="goal-track"><div className="goal-fill" style={{ width:`${pct}%` }} /></div>
                            <div style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>{pct.toFixed(0)}% of monthly target</div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Top services this month */}
              <div className="panel" style={{ marginTop:20 }}>
                <div className="panel-header">
                  <h3>TOP SERVICES — THIS MONTH</h3>
                  <span style={{ fontSize:12, color:"var(--muted)" }}>{monthServiceEntries.length} {monthServiceEntries.length === 1 ? "service" : "services"}</span>
                </div>
                {monthServiceEntries.length === 0 ? (
                  <div className="empty-state"><div className="e-icon">📊</div><p>No completed jobs yet this month.</p></div>
                ) : (
                  <div style={{ padding:"16px 20px", display:"flex", flexDirection:"column", gap:12 }}>
                    {monthServiceEntries.map(([name, total]) => (
                      <div key={name}>
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
                          <span>{name}</span>
                          <span style={{ fontFamily:"var(--head)", color:"var(--teal)", fontWeight:700 }}>{fmtMoney(total)}</span>
                        </div>
                        <div className="earnings-bar-wrap">
                          <div className="earnings-bar" style={{ width:`${(total/monthServiceMax)*100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── AVAILABILITY ─────────────────────────── */}
          {tab === "availability" && (
            <>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
                <div>
                  <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2 }}>AVAILABILITY</h2>
                  <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>
                    {blockedDates.length > 0 ? `${blockedDates.length} day${blockedDates.length !== 1 ? "s" : ""} blocked` : "All days available — tap a date to block it"}
                  </div>
                </div>
                {blockedDates.length > 0 && (
                  <button className="btn-edit" style={{ fontSize:12, color:"var(--danger)", borderColor:"rgba(239,68,68,.3)" }}
                    onClick={async () => {
                      await supabase.from("blocked_times").delete().eq("detailer_id", detailer.id);
                      setBlockedDates([]);
                      showToast("All blocks cleared ✓");
                    }}>
                    Clear All Blocks
                  </button>
                )}
              </div>

              <div className="avail-grid">
                {/* Calendar */}
                <div className="panel">
                  <div className="panel-header">
                    <button className="btn-edit" onClick={() => {
                      if (availCalMonth === 0) { setAvailCalMonth(11); setAvailCalYear(y=>y-1); }
                      else setAvailCalMonth(m=>m-1);
                    }}>‹</button>
                    <h3>{MONTH_NAMES_FULL[availCalMonth]} {availCalYear}</h3>
                    <button className="btn-edit" onClick={() => {
                      if (availCalMonth === 11) { setAvailCalMonth(0); setAvailCalYear(y=>y+1); }
                      else setAvailCalMonth(m=>m+1);
                    }}>›</button>
                  </div>
                  <div style={{ padding:"16px" }}>
                    <div className="avail-cal-grid">
                      {WEEK_DAYS_SHORT.map(d => <div className="avail-day-label" key={d}>{d}</div>)}
                      {Array.from({ length: availFirstDay(availCalYear, availCalMonth) }).map((_,i) => <div key={`e${i}`} />)}
                      {Array.from({ length: availDaysInMonth(availCalYear, availCalMonth) }, (_,i) => i+1).map(day => {
                        const dateStr = toIsoDate(availCalYear, availCalMonth, day);
                        const isBlocked   = blockedDates.includes(dateStr);
                        const hasBooking  = bookingDatesThisMonth.includes(day);
                        const isPast      = isPastDay(availCalYear, availCalMonth, day);
                        const isToday     = availCalYear === todayDate.getFullYear() && availCalMonth === todayDate.getMonth() && day === todayDate.getDate();
                        return (
                          <button key={day}
                            className={`avail-day ${isBlocked?"blocked":""} ${hasBooking&&!isBlocked?"has-booking":""} ${isToday?"today":""} ${isPast?"past":""}`}
                            onClick={() => !isPast && toggleBlockDate(availCalYear, availCalMonth, day)}
                            disabled={isPast}>
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    <div className="avail-legend">
                      <div className="avail-legend-item"><div className="avail-legend-dot" style={{ background:"rgba(239,68,68,.4)" }}/> Blocked</div>
                      <div className="avail-legend-item"><div className="avail-legend-dot" style={{ background:"rgba(59,130,246,.3)" }}/> Has booking</div>
                      <div className="avail-legend-item"><div className="avail-legend-dot" style={{ border:"2px solid var(--teal)", background:"transparent" }}/> Today</div>
                    </div>
                  </div>
                </div>

                {/* Blocked dates list */}
                <div className="panel">
                  <div className="panel-header"><h3>BLOCKED DATES</h3><span style={{ fontSize:12, color:"var(--muted)" }}>{blockedDates.length} total</span></div>
                  {blockedDates.length === 0 ? (
                    <div className="empty-state"><div className="e-icon">✅</div><p>No dates blocked.<br/>Tap any date on the calendar to block it.</p></div>
                  ) : (
                    <div style={{ maxHeight:400, overflowY:"auto" }}>
                      {[...blockedDates].sort().map(date => (
                        <div key={date} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 20px", borderBottom:"1px solid var(--border)", fontSize:14 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <span style={{ fontSize:18 }}>🚫</span>
                            <span>{formatDate(date)}</span>
                          </div>
                          <button className="btn-del" style={{ fontSize:12, padding:"4px 10px" }}
                            onClick={async () => {
                              // Delete by exact stored string so legacy/malformed rows
                              // (from the pre-fix unblock-arg-order bug) still clear.
                              await supabase.from("blocked_times")
                                .delete()
                                .eq("detailer_id", detailer.id)
                                .eq("date", date);
                              setBlockedDates(prev => prev.filter(d => d !== date));
                              showToast("Date unblocked ✓");
                            }}>
                            Unblock
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick block range */}
              <div className="panel" style={{ marginTop:20 }}>
                <div className="panel-header"><h3>QUICK BLOCK</h3><span style={{ fontSize:12, color:"var(--muted)" }}>Block a range of days at once</span></div>
                <div style={{ padding:20, display:"flex", gap:12, flexWrap:"wrap", alignItems:"flex-end" }}>
                  {[
                    { label:"Block Today",     days:1 },
                    { label:"Block Tomorrow",  days:2, offset:1 },
                    { label:"Block This Week", days:7 },
                    { label:"Block Next 2 Weeks", days:14 },
                  ].map(({ label, days, offset=0 }) => (
                    <button key={label} className="btn-edit" style={{ fontSize:13 }}
                      onClick={async () => {
                        const toBlock = [];
                        for (let i = offset; i < offset + (label === "Block Tomorrow" ? 1 : days); i++) {
                          const d = new Date(todayDate); d.setDate(d.getDate() + i);
                          const str = toIsoDate(d.getFullYear(), d.getMonth(), d.getDate());
                          if (!blockedDates.includes(str)) toBlock.push(str);
                        }
                        if (toBlock.length === 0) { showToast("Already blocked."); return; }
                        await supabase.from("blocked_times").insert(
                          toBlock.map(date => ({ detailer_id: detailer.id, date, time: "all-day" }))
                        );
                        setBlockedDates(prev => [...prev, ...toBlock]);
                        showToast(`${toBlock.length} day${toBlock.length !== 1 ? "s" : ""} blocked ✓`);
                      }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── SERVICES ─────────────────────────────── */}
          {tab === "services" && (
            <>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
                <div>
                  <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2 }}>SERVICES</h2>
                  <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>
                    {isPro ? "Unlimited services (Pro)" : `${activeServices.length}/3 services (Free)`}
                  </div>
                </div>
                {canAddService
                  ? <button className="btn-primary" onClick={openNewService}>+ Add Service</button>
                  : null
                }
              </div>

              <div className="svc-list">
                {services.map(svc => {
                  const prices = svc.service_pricing ?? [];
                  const priceStr = VEHICLES.map(v => {
                    const p = prices.find(p => p.vehicle_type === v.id);
                    return p ? `${v.label.split("/")[0].trim()} $${p.price}` : null;
                  }).filter(Boolean).join(" · ");
                  return (
                    <div className="svc-card" key={svc.id}>
                      <div className="svc-icon">{svc.icon}</div>
                      <div className="svc-info">
                        <div className="svc-name">{svc.name}</div>
                        <div className="svc-desc">{svc.description}</div>
                        {priceStr && <div className="svc-prices">{priceStr}</div>}
                        {svc.duration && <div className="svc-prices">⏱ {svc.duration}</div>}
                      </div>
                      <div className="svc-actions">
                        <button className="btn-edit" onClick={() => openEditService(svc)}>Edit</button>
                        <button className="btn-del"  onClick={() => deleteService(svc.id)}>✕</button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {!canAddService && (
                <div className="plan-gate" style={{ marginTop:20 }}>
                  <p>You've reached the 3-service limit on the free plan.</p>
                  <button className="btn-primary" onClick={() => { setTab("settings"); setBillingCycle("monthly"); }}>
                    Upgrade to Pro — $29/mo →
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── INVENTORY (Pro+) ──────────────────────── */}
          {tab === "inventory" && isProPlus && (
            <>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
                <div>
                  <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2 }}>INVENTORY</h2>
                  <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>
                    {inventory.length} {inventory.length === 1 ? "item" : "items"}
                    {lowStockCount > 0 && <span style={{ color:"#ef4444", marginLeft:8 }}>· {lowStockCount} low</span>}
                  </div>
                </div>
                <button className="btn-primary" onClick={openNewInventory}>+ Add Item</button>
              </div>

              {inventoryValue > 0 && (
                <div className="stats-grid" style={{ marginBottom:20 }}>
                  <div className="stat-card">
                    <div className="stat-label">Inventory Value</div>
                    <div className="stat-val teal">{fmtMoney(inventoryValue)}</div>
                  </div>
                  {lowStockCount > 0 && (
                    <div className="stat-card">
                      <div className="stat-label">Low Stock</div>
                      <div className="stat-val warn">{lowStockCount}</div>
                    </div>
                  )}
                </div>
              )}

              {inventory.length === 0 ? (
                <div className="empty-state">
                  <div className="e-icon">📦</div>
                  <p>No products tracked yet.</p>
                  <button className="btn-primary" style={{ marginTop:12 }} onClick={openNewInventory}>+ Add Your First Item</button>
                </div>
              ) : (
                <div className="inv-list">
                  {inventory.map(item => {
                    const low = isLowStock(item);
                    return (
                      <div key={item.id} className={`inv-card ${low ? "low" : ""}`}>
                        <div className="inv-info">
                          <div className="inv-name">
                            {item.name}
                            {low && <span className="inv-low-chip">⚠ LOW</span>}
                          </div>
                          <div className="inv-meta">
                            {item.cost_per_unit != null && <>${Number(item.cost_per_unit).toFixed(2)}/{item.unit} · </>}
                            {item.low_stock_threshold != null && <>low at {item.low_stock_threshold} {item.unit} · </>}
                            {item.notes ? item.notes : "no notes"}
                          </div>
                        </div>
                        <div className="inv-qty-block">
                          <button className="inv-qty-btn" onClick={() => adjustInventoryQty(item, -1)} disabled={Number(item.quantity) <= 0}>−</button>
                          <div style={{ textAlign:"center" }}>
                            <div className="inv-qty-val">{Number(item.quantity).toLocaleString("en-US", { maximumFractionDigits: 2 })}</div>
                            <div className="inv-qty-unit">{item.unit}</div>
                          </div>
                          <button className="inv-qty-btn" onClick={() => adjustInventoryQty(item, 1)}>+</button>
                        </div>
                        <div className="inv-actions">
                          <button className="btn-edit" onClick={() => openEditInventory(item)}>Edit</button>
                          <button className="btn-del"  onClick={() => deleteInventory(item.id)}>✕</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ── AI QUOTES (Pro+) ──────────────────────── */}
          {tab === "aiquotes" && isProPlus && (
            <>
              <div style={{ marginBottom:24 }}>
                <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2 }}>AI QUOTES</h2>
                <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>
                  {aiQuotes.length} {aiQuotes.length === 1 ? "submitted quote" : "submitted quotes"} · shareable link:{" "}
                  <a
                    href={`/book/${detailer?.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color:"var(--blue)", textDecoration:"none" }}
                  >
                    /book/{detailer?.slug}
                  </a>{" "}
                  <span style={{ color:"var(--muted)" }}>(button appears for customers)</span>
                </div>
              </div>

              {/* PRICING RULES */}
              <div className="panel" style={{ marginBottom:24 }}>
                <div className="panel-header">
                  <h3>QUOTE PRICING RULES</h3>
                  <button
                    className="btn-primary"
                    onClick={savePricingRules}
                    disabled={savingPricingRules || !pricingRules}
                    style={{ padding:"8px 18px", fontSize:13 }}
                  >
                    {savingPricingRules ? "Saving…" : "Save"}
                  </button>
                </div>
                <div style={{ padding:"22px 20px" }}>
                  <div style={{ color:"var(--muted)", fontSize:13, marginBottom:18, lineHeight:1.6 }}>
                    The AI uses these as the price range floor / ceiling per vehicle body type, then picks add-ons it can justify from the photos.
                  </div>

                  <div style={{ fontSize:12, fontWeight:700, color:"var(--blue)", letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>
                    Base Prices
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:12, marginBottom:24 }}>
                    {["coupe","sedan","suv","pickup","minivan"].map(bt => {
                      const v = pricingRules?.base_prices?.[bt] || { min: "", max: "" };
                      return (
                        <div key={bt} style={{ background:"var(--card2)", border:"1px solid var(--border)", borderRadius:12, padding:14 }}>
                          <div style={{ fontWeight:600, fontSize:13, marginBottom:10, textTransform:"capitalize" }}>{bt}</div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                            <div className="form-group">
                              <label>Min $</label>
                              <input
                                type="number"
                                min="0"
                                value={v.min ?? ""}
                                onChange={e => updateBasePrice(bt, "min", e.target.value)}
                              />
                            </div>
                            <div className="form-group">
                              <label>Max $</label>
                              <input
                                type="number"
                                min="0"
                                value={v.max ?? ""}
                                onChange={e => updateBasePrice(bt, "max", e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"var(--blue)", letterSpacing:2, textTransform:"uppercase" }}>
                      Add-Ons
                    </div>
                    <button type="button" className="btn-edit" onClick={addAddOn}>+ Add</button>
                  </div>
                  {(pricingRules?.add_ons || []).length === 0 ? (
                    <div style={{ color:"var(--muted)", fontSize:13, padding:"8px 0" }}>No add-ons configured.</div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {(pricingRules?.add_ons || []).map((a, i) => (
                        <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 120px 60px", gap:10, alignItems:"end", background:"var(--card2)", border:"1px solid var(--border)", borderRadius:10, padding:12 }}>
                          <div className="form-group">
                            <label>Label</label>
                            <input
                              type="text"
                              value={a.label || ""}
                              onChange={e => updateAddOn(i, "label", e.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <label>Price $</label>
                            <input
                              type="number"
                              min="0"
                              value={a.price ?? ""}
                              onChange={e => updateAddOn(i, "price", e.target.value)}
                            />
                          </div>
                          <button type="button" className="btn-del" onClick={() => removeAddOn(i)} style={{ height:42 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* SUBMITTED QUOTES */}
              <div className="panel">
                <div className="panel-header">
                  <h3>SUBMITTED QUOTES</h3>
                </div>
                {aiQuotes.length === 0 ? (
                  <div className="empty-state" style={{ padding:"40px 20px" }}>
                    <div className="e-icon">✨</div>
                    <p>No quotes yet. Customers can submit a quote from your booking page using the "Get Instant AI Quote" button.</p>
                  </div>
                ) : (
                  <div style={{ padding:"4px 0" }}>
                    {aiQuotes.map(q => {
                      const expanded = expandedQuoteId === q.id;
                      const vehicleStr = [q.vehicle_make, q.vehicle_model].filter(Boolean).join(" ") || q.vehicle_body_type || "Unknown";
                      const created = q.created_at ? new Date(q.created_at).toLocaleString() : "";
                      return (
                        <div key={q.id} style={{ borderBottom:"1px solid var(--border)" }}>
                          <div
                            style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, padding:"14px 20px", cursor:"pointer", flexWrap:"wrap" }}
                            onClick={() => setExpandedQuoteId(expanded ? null : q.id)}
                          >
                            <div style={{ minWidth:0, flex:1 }}>
                              <div style={{ fontWeight:600, fontSize:14 }}>{q.customer_name || "Anonymous"} · {vehicleStr}</div>
                              <div style={{ color:"var(--muted)", fontSize:12, marginTop:2 }}>
                                {q.customer_email} {q.customer_phone ? `· ${q.customer_phone}` : ""}
                                {created && <> · {created}</>}
                              </div>
                            </div>
                            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                              <div style={{ fontFamily:"var(--head)", fontWeight:700, color:"var(--teal)", fontSize:16 }}>
                                {q.quote_low != null && q.quote_high != null ? `$${q.quote_low}–$${q.quote_high}` : "—"}
                              </div>
                              <button
                                className="btn-del"
                                onClick={(e) => { e.stopPropagation(); deleteAiQuote(q.id); }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                          {expanded && (
                            <div style={{ padding:"4px 20px 20px", background:"var(--card2)" }}>
                              {q.condition_notes && (
                                <div style={{ background:"rgba(59,130,246,.06)", border:"1px solid rgba(59,130,246,.2)", borderRadius:10, padding:"10px 12px", fontSize:13, color:"#cbd5e1", marginBottom:12 }}>
                                  {q.condition_notes}
                                </div>
                              )}
                              {Array.isArray(q.line_items) && q.line_items.length > 0 && (
                                <div style={{ marginBottom:12 }}>
                                  {q.line_items.map((li, i) => (
                                    <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", fontSize:13, borderBottom:"1px solid var(--border)" }}>
                                      <span style={{ color:"#cbd5e1" }}>{li.label}</span>
                                      <span style={{ fontWeight:600 }}>${li.price}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:12, fontSize:12, color:"var(--muted)" }}>
                                <span>Confidence: <span style={{ color:"var(--text)", textTransform:"capitalize" }}>{q.confidence || "—"}</span></span>
                                {q.needs_followup && <span style={{ color:"#fbbf24" }}>· Needs follow-up</span>}
                                {q.suggested_service_name && <span>· Suggested: <span style={{ color:"var(--text)" }}>{q.suggested_service_name}</span></span>}
                              </div>
                              {Array.isArray(q.photos) && q.photos.length > 0 && (
                                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))", gap:8 }}>
                                  {q.photos.map((p, i) => (
                                    <a key={i} href={p.url} target="_blank" rel="noreferrer" style={{ display:"block", borderRadius:8, overflow:"hidden", aspectRatio:"4/3", background:"#000" }}>
                                      <img src={p.url} alt={p.label || ""} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── SETTINGS ─────────────────────────────── */}
          {tab === "settings" && settingsForm && (
            <>
              <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2, marginBottom:24 }}>SETTINGS</h2>
              <div className="settings-grid">
                <div className="panel">
                  <div className="panel-header"><h3>BUSINESS INFO</h3></div>
                  <div style={{ padding:"24px 20px" }}>
                    <form onSubmit={saveSettings}>
                      <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:20 }}>
                        <div className="form-group">
                          <label>Business Name</label>
                          <input type="text" value={settingsForm.business_name}
                            onChange={e => setSettingsForm(f => ({ ...f, business_name:e.target.value }))} required />
                        </div>
                        <div className="form-group">
                          <label>Phone</label>
                          <input type="tel" value={settingsForm.phone}
                            onChange={e => setSettingsForm(f => ({ ...f, phone:e.target.value }))} />
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                          <div className="form-group">
                            <label>City</label>
                            <input type="text" value={settingsForm.city}
                              onChange={e => setSettingsForm(f => ({ ...f, city:e.target.value }))} />
                          </div>
                          <div className="form-group">
                            <label>State</label>
                            <input type="text" value={settingsForm.state} maxLength={2}
                              onChange={e => setSettingsForm(f => ({ ...f, state:e.target.value }))} />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Bio / About (shown on booking page)</label>
                          <textarea value={settingsForm.bio}
                            onChange={e => setSettingsForm(f => ({ ...f, bio:e.target.value }))}
                            placeholder="Tell customers about your business..." />
                        </div>
                        {isPro && (
                          <>
                            {/* Stripe Connect */}
                            <div style={{ background:"var(--card2)", border:"1px solid var(--border)", borderRadius:12, padding:16 }}>
                              <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>💳 Stripe Account</div>
                              {detailer?.stripe_connected ? (
                                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                                  <div>
                                    <span style={{ color:"var(--success)", fontSize:13 }}>✓ Connected</span>
                                    <span style={{ color:"var(--muted)", fontSize:12, marginLeft:8 }}>Deposits go directly to your Stripe account.</span>
                                  </div>
                                  <button type="button" className="btn-del"
                                    style={{ fontSize:12, padding:"5px 12px", whiteSpace:"nowrap" }}
                                    onClick={handleUnlinkStripe}>
                                    Unlink
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div style={{ color:"var(--muted)", fontSize:13, marginBottom:10 }}>Connect your Stripe account so deposit payments go directly to your bank.</div>
                                  <button type="button" className="btn-primary" style={{ fontSize:13, padding:"9px 18px" }}
                                    onClick={async () => {
                                      const { data: { session: authSess } } = await supabase.auth.getSession();
                                      const token = authSess?.access_token;
                                      const res  = await fetch(`/api/stripe-account?detailer_id=${detailer.id}`, {
                                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                                      });
                                      const data = await res.json();
                                      if (data.url) window.location.href = data.url;
                                    }}>
                                    Connect Stripe →
                                  </button>
                                </>
                              )}
                            </div>

                            {/* Deposit toggle — only shown if Stripe is connected */}
                            {detailer?.stripe_connected && (
                              <div style={{ background:"var(--card2)", border:"1px solid var(--border)", borderRadius:12, padding:16 }}>
                                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                                  <div>
                                    <div style={{ fontWeight:600, fontSize:14 }}>Require $50 Deposit</div>
                                    <div style={{ color:"var(--muted)", fontSize:12, marginTop:3 }}>Customers pay $50 upfront to secure their booking. Protects against no-shows and last-minute cancellations.</div>
                                  </div>
                                  <label style={{ position:"relative", display:"inline-block", width:44, height:24, flexShrink:0 }}>
                                    <input type="checkbox" checked={settingsForm.require_deposit}
                                      onChange={e => setSettingsForm(f => ({ ...f, require_deposit:e.target.checked }))}
                                      style={{ opacity:0, width:0, height:0 }} />
                                    <span style={{
                                      position:"absolute", cursor:"pointer", inset:0, borderRadius:24,
                                      background: settingsForm.require_deposit ? "var(--blue)" : "var(--border)",
                                      transition:".3s"
                                    }}>
                                      <span style={{
                                        position:"absolute", height:18, width:18, left: settingsForm.require_deposit ? 23 : 3,
                                        bottom:3, background:"#fff", borderRadius:"50%", transition:".3s"
                                      }} />
                                    </span>
                                  </label>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        {!isPro && (
                          <div style={{ background:"rgba(59,130,246,.06)", border:"1px dashed rgba(59,130,246,.3)", borderRadius:12, padding:16 }}>
                            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>💳 Deposit Protection <span style={{ fontSize:11, background:"rgba(59,130,246,.15)", color:"var(--blue)", borderRadius:4, padding:"2px 7px", marginLeft:6 }}>PRO</span></div>
                            <div style={{ color:"var(--muted)", fontSize:13 }}>Require a $50 deposit at booking to protect against no-shows. Upgrade to Pro to enable.</div>
                          </div>
                        )}

                        {/* Square Payment Links */}
                        <div style={{ background:"var(--card2)", border:"1px solid var(--border)", borderRadius:12, padding:16 }}>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                            <div>
                              <div style={{ fontWeight:600, fontSize:14 }}>◼ Square Payments</div>
                              <div style={{ color:"var(--muted)", fontSize:12, marginTop:3 }}>
                                Send Square checkout links at job close-out. Customer pays via Apple Pay, Google Pay, or card.
                              </div>
                            </div>
                            <label style={{ position:"relative", display:"inline-block", width:44, height:24, flexShrink:0 }}>
                              <input type="checkbox" checked={settingsForm.square_enabled}
                                onChange={e => setSettingsForm(f => ({ ...f, square_enabled: e.target.checked }))}
                                style={{ opacity:0, width:0, height:0 }} />
                              <span style={{ position:"absolute", cursor:"pointer", inset:0, borderRadius:24, background: settingsForm.square_enabled ? "var(--blue)" : "var(--border)", transition:".3s" }}>
                                <span style={{ position:"absolute", height:18, width:18, left: settingsForm.square_enabled ? 23 : 3, bottom:3, background:"#fff", borderRadius:"50%", transition:".3s" }} />
                              </span>
                            </label>
                          </div>

                          {settingsForm.square_enabled && (
                            <div style={{ marginTop:14, display:"flex", flexDirection:"column", gap:12 }}>
                              {detailer?.square_access_token ? (
                                <div style={{ background:"rgba(34,197,94,.1)", border:"1px solid rgba(34,197,94,.3)", borderRadius:10, padding:"10px 14px", fontSize:13, color:"var(--success)" }}>
                                  ✓ Square payments are active — links route to your Square account.
                                </div>
                              ) : (
                                <div style={{ background:"rgba(245,158,11,.08)", border:"1px solid rgba(245,158,11,.3)", borderRadius:10, padding:"10px 14px", fontSize:13, color:"var(--warn)" }}>
                                  Paste your Square Production Access Token below and hit <strong>Save Settings</strong> to activate.
                                </div>
                              )}

                              <div className="form-group" style={{ marginBottom:0 }}>
                                <label style={{ fontSize:11, letterSpacing:1, textTransform:"uppercase" }}>Square Production Access Token</label>
                                <input
                                  type="password"
                                  autoComplete="off"
                                  spellCheck={false}
                                  placeholder="EAAAl..."
                                  value={settingsForm.square_access_token}
                                  onChange={e => setSettingsForm(f => ({ ...f, square_access_token: e.target.value.trim() }))}
                                  style={{ background:"var(--card)", border:"2px solid var(--border)", borderRadius:8, padding:"9px 12px", color:"var(--text)", fontFamily:"monospace", fontSize:13, outline:"none", width:"100%", marginTop:6 }}
                                />
                              </div>

                              <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 14px", fontSize:12, color:"var(--muted)", lineHeight:1.8 }}>
                                <strong style={{ color:"var(--text)" }}>How to get your token:</strong><br/>
                                1. Go to <a href="https://developer.squareup.com/apps" target="_blank" rel="noopener noreferrer" style={{ color:"var(--blue)" }}>developer.squareup.com</a> and sign in<br/>
                                2. Open your app (or create a new one)<br/>
                                3. Click <strong style={{ color:"var(--text)" }}>Credentials</strong> in the left menu<br/>
                                4. Under <strong style={{ color:"var(--text)" }}>Production</strong>, copy your <strong style={{ color:"var(--text)" }}>Production Access Token</strong><br/>
                                5. Paste it above and hit <strong style={{ color:"var(--text)" }}>Save Settings</strong>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="settings-save-sticky">
                        <button className="btn-primary full" type="submit">Save Settings</button>
                      </div>
                    </form>
                  </div>
                </div>

                {/* ── SERVICE AREA MAP (Pro) ──────────────── */}
                {isPro ? (
                  <div className="panel">
                    <div className="panel-header">
                      <h3>SERVICE AREA</h3>
                      <span style={{ fontSize:12, color:"var(--muted)" }}>Pro · Google Maps</span>
                    </div>
                    <div style={{ padding:"20px" }}>
                      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                        <div className="form-group">
                          <label>Home Base Address</label>
                          <input type="text"
                            placeholder="e.g. 123 Main St, Akron, OH"
                            value={settingsForm.service_address}
                            onChange={e => setSettingsForm(f => ({ ...f, service_address:e.target.value }))} />
                          <span style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>Customers will see if they're within your service area.</span>
                        </div>
                        <div className="form-group">
                          <label>Service Radius (miles): {settingsForm.service_radius} mi</label>
                          <input type="range" min={5} max={100} step={5}
                            value={settingsForm.service_radius}
                            onChange={e => setSettingsForm(f => ({ ...f, service_radius: parseInt(e.target.value) }))}
                            style={{ width:"100%", accentColor:"var(--blue)" }} />
                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"var(--muted)" }}>
                            <span>5 mi</span><span>50 mi</span><span>100 mi</span>
                          </div>
                        </div>
                        {/* Map preview with live radius circle */}
                        <div className="map-container" style={{ position:"relative" }}>
                          <div ref={mapDivRef} style={{ width:"100%", height:"100%", borderRadius:12, overflow:"hidden" }} />
                          {!settingsForm.service_address && (
                            <div className="map-placeholder" style={{ position:"absolute", inset:0 }}>
                              <span style={{ fontSize:32 }}>🗺️</span>
                              <span>{GOOGLE_MAPS_KEY ? "Enter your address above to preview your service area" : "Add VITE_GOOGLE_MAPS_KEY to enable maps"}</span>
                            </div>
                          )}
                        </div>
                        <button className="btn-primary" onClick={async () => {
                          await supabase.from("detailers").update({
                            service_address: settingsForm.service_address,
                            service_radius:  settingsForm.service_radius,
                            service_lat:     settingsForm.service_lat,
                            service_lng:     settingsForm.service_lng,
                          }).eq("id", detailer.id);
                          setDetailer(d => ({ ...d, service_address: settingsForm.service_address, service_radius: settingsForm.service_radius, service_lat: settingsForm.service_lat, service_lng: settingsForm.service_lng }));
                          showToast("Service area saved! ✓");
                        }}>Save Service Area</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="panel">
                    <div className="panel-header"><h3>SERVICE AREA MAP</h3></div>
                    <div style={{ padding:20 }}>
                      <div style={{ background:"rgba(59,130,246,.06)", border:"1px dashed rgba(59,130,246,.3)", borderRadius:12, padding:20, textAlign:"center" }}>
                        <div style={{ fontSize:28, marginBottom:10 }}>🗺️</div>
                        <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>Service Area Radius <span style={{ fontSize:11, background:"rgba(59,130,246,.15)", color:"var(--blue)", borderRadius:4, padding:"2px 7px", marginLeft:4 }}>PRO</span></div>
                        <div style={{ color:"var(--muted)", fontSize:13, marginBottom:14 }}>Set your home base and service radius. Customers see if they're in range before booking.</div>
                        <button className="btn-primary" style={{ fontSize:13 }} onClick={() => { setTab("settings"); setBillingCycle("monthly"); }}>Upgrade to Pro →</button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="panel">
                  <div className="panel-header"><h3>PLAN & BILLING</h3></div>
                  <div style={{ padding:"24px 20px" }}>
                    {isPro ? (
                      <>
                        {/* Trial countdown banner */}
                        {isTrialing && trialDaysLeft !== null && (
                          <div style={{ background:"rgba(59,130,246,.1)", border:"1px solid rgba(59,130,246,.3)", borderRadius:10, padding:"12px 16px", marginBottom:16, display:"flex", alignItems:"center", gap:12 }}>
                            <div style={{ fontSize:28, lineHeight:1 }}>⏳</div>
                            <div>
                              <div style={{ fontWeight:700, fontSize:14 }}>
                                {trialDaysLeft === 0 ? "Trial ends today!" : `${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left in your free trial`}
                              </div>
                              <div style={{ color:"var(--muted)", fontSize:12, marginTop:2 }}>Your card will be charged when the trial ends. Cancel anytime.</div>
                            </div>
                          </div>
                        )}
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                          <div>
                            <div style={{ fontWeight:700, fontSize:15 }}>PRO Plan</div>
                            <div style={{ color:"var(--muted)", fontSize:13, marginTop:3 }}>
                              {detailer?.subscription_status === "past_due" ? "⚠️ Payment past due — update your card"
                               : isTrialing ? "Free trial · All features unlocked"
                               : "All features unlocked · Active"}
                            </div>
                          </div>
                          <span className="badge pro">{isTrialing ? "TRIAL" : "ACTIVE"}</span>
                        </div>
                        <button className="btn-edit" style={{ width:"100%", justifyContent:"center", padding:"10px" }}
                          onClick={handleManageSubscription}>
                          Manage Subscription →
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Billing toggle */}
                        <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}>
                          <div style={{ background:"var(--card2)", borderRadius:10, padding:3, display:"flex", gap:2 }}>
                            {[["monthly","Monthly"],["annual","Annual · 2 months free"]].map(([val,label]) => (
                              <button key={val}
                                onClick={() => setBillingCycle(val)}
                                style={{
                                  padding:"7px 16px", borderRadius:8, border:"none", cursor:"pointer",
                                  fontFamily:"var(--body)", fontSize:13, fontWeight:500, transition:"all .2s",
                                  background: billingCycle === val ? "var(--blue)" : "transparent",
                                  color: billingCycle === val ? "#fff" : "var(--muted)",
                                }}>
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Trial banner */}
                        <div style={{ background:"rgba(59,130,246,.08)", border:"1px solid rgba(59,130,246,.25)", borderRadius:10, padding:"10px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ fontSize:20 }}>🎁</span>
                          <div style={{ fontSize:13 }}><strong>7-day free trial</strong> — no charge until your trial ends. Cancel anytime.</div>
                        </div>

                        {/* Pricing card */}
                        <div style={{ border:"1px solid rgba(59,130,246,.4)", borderRadius:14, padding:"20px", background:"rgba(59,130,246,.04)", marginBottom:16 }}>
                          <div style={{ display:"flex", alignItems:"baseline", gap:4, marginBottom:4 }}>
                            <span style={{ fontFamily:"var(--head)", fontSize:40, fontWeight:800 }}>
                              {billingCycle === "monthly" ? "$29" : "$290"}
                            </span>
                            <span style={{ color:"var(--muted)", fontSize:14 }}>
                              /{billingCycle === "monthly" ? "mo" : "yr"}
                            </span>
                          </div>
                          {billingCycle === "annual" && (
                            <div style={{ color:"var(--teal)", fontSize:13, marginBottom:10 }}>Save $58 — 2 months free</div>
                          )}
                          <div style={{ display:"flex", flexDirection:"column", gap:8, margin:"16px 0", fontSize:14 }}>
                            {["✓ Unlimited services","✓ $50 deposit protection","✓ Stripe Connect payouts","✓ No NEO Detail branding","✓ Priority support"].map(f => (
                              <div key={f} style={{ color:"var(--text)" }}>{f}</div>
                            ))}
                          </div>
                          <button className="btn-primary full"
                            onClick={() => handleUpgrade(billingCycle === "monthly" ? PRICE_MONTHLY : PRICE_ANNUAL)}>
                            Start 7-Day Free Trial →
                          </button>
                        </div>
                        <div style={{ color:"var(--muted)", fontSize:12, textAlign:"center" }}>Cancel anytime · Instant access · No contracts</div>
                      </>
                    )}
                  </div>
                </div>

                <div className="panel">
                  <div className="panel-header"><h3>YOUR BOOKING LINK</h3></div>
                  <div style={{ padding:"20px" }}>
                    <div className="link-box">
                      <span>{bookingUrl}</span>
                      <button className="btn-edit" onClick={() => { navigator.clipboard.writeText(bookingUrl); showToast("Copied! ✓"); }}>Copy</button>
                      <a href={bookingUrl} target="_blank" rel="noreferrer"><button className="btn-edit">View →</button></a>
                    </div>
                    <p style={{ color:"var(--muted)", fontSize:13, marginTop:12 }}>Share this link with customers so they can book online.</p>
                  </div>
                </div>

                {/* Sign out — visible on mobile since top nav button is hidden */}
                <div style={{ textAlign:"center", paddingBottom:8 }}>
                  <button className="btn-ghost" onClick={signOut}
                    style={{ fontSize:13, color:"var(--muted)" }}>
                    Sign Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── BOOKING DETAIL MODAL ──────────────────────── */}
      {selectedAppt && (
        <div className="modal-overlay" onClick={() => setSelectedAppt(null)}>
          <div className="modal fade-in" onClick={e => e.stopPropagation()}>
            <h3>BOOKING DETAILS</h3>
            {[
              ["Customer", selectedAppt.name],
              ["Email",    selectedAppt.email],
              ["Phone",    selectedAppt.phone || "—"],
              ["Service",  selectedAppt.service_name],
              ["Vehicle",  selectedAppt.vehicle_label],
              ["Date",     formatDate(selectedAppt.date)],
              ["Time",     selectedAppt.time],
              ["Address",  selectedAppt.address || "—"],
              ["Notes",    selectedAppt.notes || "—"],
              ["Status",   selectedAppt.status],
            ].map(([k,v]) => (
              <div className="modal-row" key={k}>
                <span style={{ color:"var(--muted)" }}>{k}</span>
                <span>{v}</span>
              </div>
            ))}

            {/* ── Weather (Pro+) ── */}
            {isProPlus && bookingWeather[selectedAppt.id] && (
              <div className="modal-row">
                <span style={{ color:"var(--muted)" }}>Weather</span>
                <span>{bookingWeather[selectedAppt.id].icon} {bookingWeather[selectedAppt.id].temp}°F</span>
              </div>
            )}

            {/* ── Inline price editor ── */}
            {(() => {
              const apptAdj  = parseFloat(selectedAppt.adjustment_amount) || 0;
              const base     = selectedAppt.amount || 0;
              const finalAmt = base + apptAdj;
              const canEdit  = selectedAppt.status !== "completed" && selectedAppt.status !== "cancelled" && selectedAppt.status !== "declined";
              return (
                <div style={{ marginTop:14, paddingTop:14, borderTop:"1px solid var(--border)", textAlign:"center" }}>
                  {editingApptPrice ? (
                    <>
                      <input
                        type="number" step="0.01" min="0" autoFocus
                        value={apptPriceEditValue}
                        onChange={e => setApptPriceEditValue(e.target.value)}
                        style={{ fontFamily:"var(--head)", fontSize:40, fontWeight:800, color:"var(--teal)", background:"transparent", border:"none", borderBottom:"2px solid var(--teal)", outline:"none", textAlign:"center", width:"100%", padding:"4px 0" }} />
                      <input
                        type="text" placeholder="Note (optional)" maxLength={60}
                        value={adjustmentReasonInput}
                        onChange={e => setAdjustmentReasonInput(e.target.value)}
                        style={{ marginTop:10, background:"var(--card2)", border:"1px solid var(--border)", borderRadius:8, padding:"9px 12px", color:"var(--text)", fontFamily:"var(--body)", fontSize:13, outline:"none", width:"100%", boxSizing:"border-box" }} />
                      <div style={{ display:"flex", gap:8, marginTop:12, justifyContent:"center" }}>
                        <button type="button" onClick={() => { setEditingApptPrice(false); setApptPriceEditValue(""); setAdjustmentReasonInput(""); }}
                          style={{ background:"transparent", color:"var(--muted)", border:"1px solid var(--border)", borderRadius:8, padding:"8px 20px", fontFamily:"var(--body)", fontWeight:600, fontSize:13, cursor:"pointer" }}>
                          Cancel
                        </button>
                        <button type="button" disabled={savingAdjustment} onClick={async () => {
                          const newTotal = parseFloat(apptPriceEditValue) || 0;
                          const newAdj   = Math.round((newTotal - base) * 100) / 100;
                          await _writeAdjustment(newAdj, adjustmentReasonInput.trim() || null);
                          setEditingApptPrice(false);
                          setApptPriceEditValue("");
                        }}
                          style={{ background:"var(--teal)", color:"#fff", border:"none", borderRadius:8, padding:"8px 24px", fontFamily:"var(--body)", fontWeight:700, fontSize:13, cursor: savingAdjustment ? "not-allowed" : "pointer", opacity: savingAdjustment ? .6 : 1 }}>
                          {savingAdjustment ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize:11, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Total</div>
                      <div
                        onClick={() => { if (canEdit) { setApptPriceEditValue(finalAmt.toFixed(2)); setAdjustmentReasonInput(selectedAppt.adjustment_reason || ""); setEditingApptPrice(true); } }}
                        style={{ fontFamily:"var(--head)", fontSize:40, fontWeight:800, color:"var(--teal)", lineHeight:1, cursor: canEdit ? "pointer" : "default", display:"inline-block" }}>
                        {fmt$(finalAmt.toFixed(2))}
                      </div>
                      {canEdit && <div style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>Tap to edit price</div>}
                      {apptAdj !== 0 && (
                        <div style={{ fontSize:12, color: apptAdj < 0 ? "var(--success)" : "var(--warn)", marginTop:6 }}>
                          Adjusted from {fmt$(base)}{selectedAppt.adjustment_reason ? ` · ${selectedAppt.adjustment_reason}` : ""}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* ── AI Job Estimator (Pro+) ── */}
            {isProPlus && selectedAppt.status !== "completed" && selectedAppt.status !== "cancelled" && selectedAppt.status !== "declined" && (
              <div style={{ marginTop:14, paddingTop:14, borderTop:"1px solid var(--border)" }}>
                <div style={{ fontSize:11, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>✨ AI Job Estimator</div>
                {aiEstimate ? (
                  <>
                    <div style={{ fontSize:13, color:"var(--muted)", lineHeight:1.6, marginBottom:12, background:"var(--card2)", borderRadius:10, padding:"10px 12px" }}>
                      {aiEstimate.reasoning}
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
                      <div>
                        <div style={{ fontSize:11, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>Price</div>
                        <input type="number" min="0" step="1" value={estimatePrice} onChange={e => setEstimatePrice(e.target.value)}
                          style={{ width:"100%", background:"var(--card)", border:"1px solid var(--border)", borderRadius:8, padding:"9px 10px", color:"var(--text)", fontFamily:"var(--head)", fontSize:20, fontWeight:800, textAlign:"center", outline:"none", boxSizing:"border-box" }} />
                      </div>
                      <div>
                        <div style={{ fontSize:11, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>Hours</div>
                        <input type="number" min="0" step="0.5" value={estimateHours} onChange={e => setEstimateHours(e.target.value)}
                          style={{ width:"100%", background:"var(--card)", border:"1px solid var(--border)", borderRadius:8, padding:"9px 10px", color:"var(--text)", fontFamily:"var(--head)", fontSize:20, fontWeight:800, textAlign:"center", outline:"none", boxSizing:"border-box" }} />
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={() => { setAiEstimate(null); setEstimatePrice(""); setEstimateHours(""); }}
                        style={{ background:"transparent", color:"var(--muted)", border:"1px solid var(--border)", borderRadius:8, padding:"9px 14px", fontFamily:"var(--body)", fontWeight:600, fontSize:13, cursor:"pointer" }}>
                        Dismiss
                      </button>
                      <button style={{ flex:1, background:"var(--teal)", color:"#fff", border:"none", borderRadius:8, padding:"9px 14px", fontFamily:"var(--body)", fontWeight:700, fontSize:13, cursor:"pointer" }}
                        onClick={async () => {
                          const newPrice = parseFloat(estimatePrice);
                          if (!isNaN(newPrice) && newPrice > 0) {
                            const newAdj = Math.round((newPrice - (selectedAppt.amount || 0)) * 100) / 100;
                            await _writeAdjustment(newAdj, `AI estimate · ${estimateHours}hrs`);
                            setAiEstimate(null);
                          }
                        }}>
                        Apply to Booking
                      </button>
                    </div>
                  </>
                ) : (
                  <button onClick={generateEstimate} disabled={generatingEstimate}
                    style={{ width:"100%", background:"rgba(99,102,241,.1)", border:"1px solid rgba(99,102,241,.3)", borderRadius:10, padding:"12px 16px", color:"#818cf8", fontFamily:"var(--body)", fontWeight:600, fontSize:14, cursor: generatingEstimate ? "not-allowed" : "pointer", opacity: generatingEstimate ? .6 : 1, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                    {generatingEstimate
                      ? <><span className="spinner" style={{ borderTopColor:"#818cf8" }} />Analyzing photos…</>
                      : "✨ Generate AI Estimate"}
                  </button>
                )}
              </div>
            )}

            <div className="modal-actions">
              {selectedAppt.status === "pending" && (
                <>
                  <button className="btn-confirm" onClick={() => updateStatus(selectedAppt.id,"confirmed")} disabled={updating}>
                    {updating ? <span className="spinner"/> : "✓"} Confirm
                  </button>
                  <button className="btn-decline" onClick={() => updateStatus(selectedAppt.id,"declined")} disabled={updating}>
                    ✕ Decline
                  </button>
                </>
              )}
              {selectedAppt.status === "confirmed" && (
                <>
                  <button
                    style={{ flex:1, background:"var(--blue)", color:"#fff", border:"none", borderRadius:10, padding:"12px", fontFamily:"var(--body)", fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}
                    onClick={() => {
                      setActiveJob(selectedAppt);
                      setJobStep("arrival");
                      setArrivalSent(false);
                      setPaymentUrl(null);
                      setSelectedAppt(null);
                    }}>
                    🚗 Start Job
                  </button>
                  <button className="btn-confirm" onClick={() => updateStatus(selectedAppt.id,"completed")} disabled={updating}
                    style={{ background:"var(--teal)" }}>
                    {updating ? <span className="spinner"/> : "✓"} Mark Complete
                  </button>
                  <button className="btn-decline" onClick={() => updateStatus(selectedAppt.id,"cancelled")} disabled={updating}>
                    ✕ Cancel
                  </button>
                </>
              )}
              {selectedAppt.status === "completed" && (
                <button className="btn-confirm" style={{ background:"var(--teal)", opacity:.6, cursor:"default" }} disabled>
                  ✓ Completed
                </button>
              )}
              {/* Photo upload for this booking */}
              {(selectedAppt.status === "completed" || selectedAppt.status === "confirmed") && (
                <label style={{ cursor:"pointer", flex:1 }}>
                  <input type="file" accept="image/*" style={{ display:"none" }}
                    onChange={async e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      // Ask type via simple prompt
                      const type = selectedAppt.status === "confirmed" ? "before" : "after";
                      await uploadPhoto(file, selectedAppt.id, type);
                    }} />
                  <span className="btn-edit" style={{ fontSize:12, display:"block", textAlign:"center", padding:"10px 12px" }}>
                    {uploadingPhoto ? "Uploading..." : selectedAppt.status === "confirmed" ? "📷 Before Photo" : "📷 After Photo"}
                  </span>
                </label>
              )}
              {/* Quick SMS to customer */}
              {isPro && selectedAppt.phone && (
                <button className="btn-edit" style={{ fontSize:12 }}
                  onClick={() => {
                    setSmsForm({ phone: selectedAppt.phone, message: `Hi ${selectedAppt.name.split(" ")[0]}! Just confirming your ${selectedAppt.service_name} on ${formatDate(selectedAppt.date)} at ${selectedAppt.time}. — ${detailer.business_name}` });
                    setTab("customers");
                    setSelectedAppt(null);
                    showToast("SMS form pre-filled — scroll down in Customers tab");
                  }}>
                  💬 SMS Customer
                </button>
              )}
              <button className="btn-close" onClick={() => setSelectedAppt(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── JOB FLOW MODAL ───────────────────────────── */}
      {activeJob && (
        <div className="modal-overlay">
          <div className="modal fade-in" onClick={e => e.stopPropagation()} style={{ maxWidth:480 }}>

            {/* Header */}
            {(() => {
              const STEPS = ["arrival","before","detailing","after","payment"];
              const titles = {
                arrival:   "📍 CONFIRM ARRIVAL",
                before:    "📷 BEFORE PHOTOS",
                detailing: "🚗 DETAIL IN PROGRESS",
                after:     "📷 AFTER PHOTOS",
                payment:   "💳 CLOSE OUT JOB",
              };
              const curIdx = STEPS.indexOf(jobStep);
              return (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                  <div>
                    <h3 style={{ margin:0 }}>{titles[jobStep]}</h3>
                    <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>
                      {activeJob.name} · {activeJob.service_name} · {activeJob.vehicle_label}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    {STEPS.map((s, i) => (
                      <div key={s} style={{
                        width:8, height:8, borderRadius:"50%",
                        background: i === curIdx ? "var(--blue)" : i < curIdx ? "var(--teal)" : "var(--border)"
                      }} />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ─ Step 1: Confirm Arrival ─ */}
            {jobStep === "arrival" && (
              <>
                <div style={{ background:"var(--card2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px 18px", marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Booking Details</div>
                  {[
                    ["Customer",  activeJob.name],
                    ["Phone",     activeJob.phone || "—"],
                    ["Address",   activeJob.address || "—"],
                    ["Service",   activeJob.service_name],
                    ["Vehicle",   activeJob.vehicle_label],
                    ["Time",      activeJob.time],
                  ].map(([k,v]) => (
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid var(--border)", fontSize:13 }}>
                      <span style={{ color:"var(--muted)" }}>{k}</span>
                      <span style={{ fontWeight:500 }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize:13, color:"var(--muted)", marginBottom:16, lineHeight:1.5 }}>
                  Tap <strong style={{ color:"var(--text)" }}>Confirm Arrival</strong> to notify the customer you're on site. An SMS will be sent to their phone automatically.
                </div>
                {arrivalSent && (
                  <div style={{ background:"rgba(34,197,94,.1)", border:"1px solid rgba(34,197,94,.3)", borderRadius:10, padding:"10px 14px", marginBottom:12, fontSize:13, color:"var(--success)" }}>
                    ✓ SMS sent to customer
                  </div>
                )}
                <div style={{ display:"flex", gap:10 }}>
                  <button className="btn-close" onClick={() => setActiveJob(null)}>Cancel</button>
                  <button className="btn-primary" style={{ flex:1 }} onClick={confirmArrival} disabled={updating}>
                    {updating ? <><span className="spinner"/>Sending SMS...</> : "📍 Confirm Arrival →"}
                  </button>
                </div>
              </>
            )}

            {/* ─ Step 2: Before photos ─ */}
            {jobStep === "before" && (() => {
              const beforePhotos = photos.filter(p => p.booking_id === activeJob.id && p.type === "before");
              const atLimit = beforePhotos.length >= 6;
              return (
                <>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <div style={{ fontSize:13, color:"var(--muted)" }}>
                      Document the vehicle before you start. (Optional)
                    </div>
                    <div style={{ fontSize:12, fontWeight:700, color: atLimit ? "var(--warn)" : "var(--muted)", flexShrink:0, marginLeft:10 }}>
                      {beforePhotos.length}/6
                    </div>
                  </div>
                  <div className="job-photos-grid">
                    {beforePhotos.map(p => (
                      <div className="job-photo-thumb" key={p.id}>
                        <img src={p.url} alt="before" loading="lazy" />
                        <button className="photo-del" onClick={() => deletePhoto(p)}>✕</button>
                      </div>
                    ))}
                    {!atLimit && (
                      <label className="job-photo-add" style={{ cursor: uploadingPhoto ? "not-allowed" : "pointer", opacity: uploadingPhoto ? .5 : 1 }}>
                        <input type="file" accept="image/*" capture="environment" style={{ display:"none" }}
                          disabled={uploadingPhoto}
                          onChange={async e => {
                            const file = e.target.files[0];
                            if (file) await uploadPhoto(file, activeJob.id, "before");
                            e.target.value = "";
                          }} />
                        <span style={{ fontSize:24 }}>📷</span>
                        <span style={{ fontSize:11 }}>{uploadingPhoto ? "Uploading..." : "Add Photo"}</span>
                      </label>
                    )}
                  </div>
                  <div style={{ display:"flex", gap:10, marginTop:16 }}>
                    <button className="btn-close" onClick={() => setJobStep("arrival")}>← Back</button>
                    <button className="btn-primary" style={{ flex:1 }}
                      onClick={() => setJobStep("detailing")}>
                      {beforePhotos.length > 0 ? "Photos Done → Start Detail" : "Skip → Start Detail"}
                    </button>
                  </div>
                </>
              );
            })()}

            {/* ─ Step 3: Detailing in progress ─ */}
            {jobStep === "detailing" && (
              <>
                <div style={{ textAlign:"center", padding:"24px 0" }}>
                  <div style={{ fontSize:56, marginBottom:16 }}>🚗✨</div>
                  <div style={{ fontFamily:"var(--head)", fontSize:22, fontWeight:800, letterSpacing:2, marginBottom:8 }}>
                    DETAIL IN PROGRESS
                  </div>
                  <div style={{ color:"var(--muted)", fontSize:14, lineHeight:1.6, marginBottom:28 }}>
                    When you finish the detail, tap <strong style={{ color:"var(--text)" }}>Detail Completed</strong> to take your after photos and close out the job.
                  </div>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <button className="btn-close" onClick={() => setJobStep("before")}>← Before Photos</button>
                  <button className="btn-primary" style={{ flex:1 }} onClick={() => setJobStep("after")}>
                    ✓ Detail Completed →
                  </button>
                </div>
              </>
            )}

            {/* ─ Step 4: After photos ─ */}
            {jobStep === "after" && (() => {
              const afterPhotos = photos.filter(p => p.booking_id === activeJob.id && p.type === "after");
              const atLimit = afterPhotos.length >= 6;
              return (
                <>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <div style={{ fontSize:13, color:"var(--muted)" }}>
                      Show off your work! Take after photos before charging the customer.
                    </div>
                    <div style={{ fontSize:12, fontWeight:700, color: atLimit ? "var(--warn)" : "var(--muted)", flexShrink:0, marginLeft:10 }}>
                      {afterPhotos.length}/6
                    </div>
                  </div>
                  <div className="job-photos-grid">
                    {afterPhotos.map(p => (
                      <div className="job-photo-thumb" key={p.id}>
                        <img src={p.url} alt="after" loading="lazy" />
                        <button className="photo-del" onClick={() => deletePhoto(p)}>✕</button>
                      </div>
                    ))}
                    {!atLimit && (
                      <label className="job-photo-add" style={{ cursor: uploadingPhoto ? "not-allowed" : "pointer", opacity: uploadingPhoto ? .5 : 1 }}>
                        <input type="file" accept="image/*" capture="environment" style={{ display:"none" }}
                          disabled={uploadingPhoto}
                          onChange={async e => {
                            const file = e.target.files[0];
                            if (file) await uploadPhoto(file, activeJob.id, "after");
                            e.target.value = "";
                          }} />
                        <span style={{ fontSize:24 }}>📷</span>
                        <span style={{ fontSize:11 }}>{uploadingPhoto ? "Uploading..." : "Add Photo"}</span>
                      </label>
                    )}
                  </div>
                  <div style={{ display:"flex", gap:10, marginTop:16 }}>
                    <button className="btn-close" onClick={() => setJobStep("detailing")}>← Back</button>
                    <button className="btn-primary" style={{ flex:1 }}
                      onClick={() => setJobStep("payment")}>
                      {afterPhotos.length > 0 ? "Photos Done → Charge Customer →" : "Skip → Charge Customer →"}
                    </button>
                  </div>
                </>
              );
            })()}

            {/* ─ Step 5: Payment & close-out ─ */}
            {jobStep === "payment" && (() => {
              const depositPaid = activeJob.deposit_paid && detailer?.require_deposit;
              const base        = activeJob.amount || 0;
              const adjAmt      = parseFloat(activeJob.adjustment_amount) || 0;
              const adjusted    = base + adjAmt;
              const remaining   = depositPaid ? Math.max(adjusted - 50, 0) : adjusted;
              const total       = remaining;
              const totalCents  = Math.round(total * 100);
              const qrUrl       = paymentUrl
                ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(paymentUrl)}&bgcolor=141920&color=e2e8f4&margin=10`
                : null;

              return (
                <>
                  {/* ── Amount hero (tap to edit) ── */}
                  <div style={{ textAlign:"center", padding:"18px 0 14px" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"var(--muted)", letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>
                      {activeJob.name} · {activeJob.vehicle_label}
                    </div>
                    {editingPrice ? (
                      <>
                        <input
                          type="number" step="0.01" min="0" autoFocus
                          value={priceEditValue}
                          onChange={e => setPriceEditValue(e.target.value)}
                          style={{ fontFamily:"var(--head)", fontSize:48, fontWeight:800, color:"var(--teal)", background:"transparent", border:"none", borderBottom:"2px solid var(--teal)", outline:"none", textAlign:"center", width:"100%", padding:"4px 0" }} />
                        <input
                          type="text" placeholder="Note (optional)" maxLength={60}
                          value={adjustmentReasonInput}
                          onChange={e => setAdjustmentReasonInput(e.target.value)}
                          style={{ marginTop:10, background:"var(--card)", border:"1px solid var(--border)", borderRadius:8, padding:"9px 12px", color:"var(--text)", fontFamily:"var(--body)", fontSize:13, outline:"none", width:"100%", boxSizing:"border-box" }} />
                        <div style={{ display:"flex", gap:8, marginTop:12, justifyContent:"center" }}>
                          <button onClick={() => { setEditingPrice(false); setPriceEditValue(""); setAdjustmentReasonInput(""); }}
                            style={{ background:"transparent", color:"var(--muted)", border:"1px solid var(--border)", borderRadius:8, padding:"8px 20px", fontFamily:"var(--body)", fontWeight:600, fontSize:13, cursor:"pointer" }}>
                            Cancel
                          </button>
                          <button disabled={savingAdjustment} onClick={async () => {
                            const newTotal = parseFloat(priceEditValue) || 0;
                            const newAdj   = depositPaid ? (newTotal + 50 - base) : (newTotal - base);
                            await _writeAdjustment(Math.round(newAdj * 100) / 100, adjustmentReasonInput.trim() || null);
                            setEditingPrice(false);
                            setPriceEditValue("");
                          }}
                            style={{ background:"var(--teal)", color:"#fff", border:"none", borderRadius:8, padding:"8px 24px", fontFamily:"var(--body)", fontWeight:700, fontSize:13, cursor: savingAdjustment ? "not-allowed" : "pointer", opacity: savingAdjustment ? .6 : 1 }}>
                            {savingAdjustment ? "Saving…" : "Save"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          onClick={() => { if (!paymentUrl) { setPriceEditValue(total.toFixed(2)); setAdjustmentReasonInput(activeJob.adjustment_reason || ""); setEditingPrice(true); } }}
                          style={{ fontFamily:"var(--head)", fontSize:52, fontWeight:800, color:"var(--teal)", lineHeight:1, cursor: paymentUrl ? "default" : "pointer", display:"inline-block" }}>
                          {fmt$(total.toFixed(2))}
                        </div>
                        {!paymentUrl && (
                          <div style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>Tap to edit price</div>
                        )}
                        {(depositPaid || adjAmt !== 0) && (
                          <div style={{ display:"flex", justifyContent:"center", gap:16, marginTop:10, flexWrap:"wrap" }}>
                            <span style={{ fontSize:12, color:"var(--muted)" }}>Service: {fmt$(base)}</span>
                            {adjAmt !== 0 && (
                              <span style={{ fontSize:12, color: adjAmt < 0 ? "var(--success)" : "var(--warn)" }}>
                                Adjusted from {fmt$(base)}{activeJob.adjustment_reason ? ` · ${activeJob.adjustment_reason}` : ""}
                              </span>
                            )}
                            {depositPaid && <span style={{ fontSize:12, color:"var(--success)" }}>Deposit: −$50</span>}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* ── Payment options ── */}
                  {!paymentUrl ? (
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:2 }}>Collect Payment</div>

                      {detailer?.square_enabled && detailer?.square_access_token && (
                        <button
                          style={{ background:"rgba(0,201,167,.1)", border:"1px solid rgba(0,201,167,.3)", borderRadius:12, padding:"13px 16px", color:"var(--teal)", fontFamily:"var(--body)", fontWeight:600, fontSize:14, cursor: generatingPayLink ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:12, transition:".15s", opacity: generatingPayLink ? .6 : 1 }}
                          onClick={() => generateSquareLink(totalCents)}
                          disabled={generatingPayLink || totalCents <= 0}>
                          <span style={{ fontSize:22, lineHeight:1 }}>◼</span>
                          <span style={{ flex:1, textAlign:"left" }}>
                            <span style={{ display:"block" }}>Square — Send Payment Link</span>
                            <span style={{ fontSize:11, fontWeight:400, color:"var(--muted)" }}>SMS'd to customer · Apple Pay, Google Pay, card</span>
                          </span>
                          {generatingPayLink
                            ? <span className="spinner" style={{ flexShrink:0 }}/>
                            : <span style={{ fontFamily:"var(--head)", fontSize:16, fontWeight:800, flexShrink:0 }}>{fmt$(total.toFixed(2))}</span>}
                        </button>
                      )}

                      {detailer?.stripe_connected && (
                        <button
                          style={{ background:"rgba(139,92,246,.1)", border:"1px solid rgba(139,92,246,.3)", borderRadius:12, padding:"13px 16px", color:"#a78bfa", fontFamily:"var(--body)", fontWeight:600, fontSize:14, cursor: generatingPayLink ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:12, transition:".15s", opacity: generatingPayLink ? .6 : 1 }}
                          onClick={() => generatePaymentLink(totalCents)}
                          disabled={generatingPayLink || totalCents <= 0}>
                          <span style={{ fontSize:22, lineHeight:1 }}>💳</span>
                          <span style={{ flex:1, textAlign:"left" }}>
                            <span style={{ display:"block" }}>Stripe — Send Payment Link</span>
                            <span style={{ fontSize:11, fontWeight:400, color:"var(--muted)" }}>SMS'd to customer · card or bank</span>
                          </span>
                          {generatingPayLink
                            ? <span className="spinner" style={{ flexShrink:0 }}/>
                            : <span style={{ fontFamily:"var(--head)", fontSize:16, fontWeight:800, flexShrink:0 }}>{fmt$(total.toFixed(2))}</span>}
                        </button>
                      )}

                      <button
                        style={{ background:"rgba(34,197,94,.08)", border:"1px solid rgba(34,197,94,.25)", borderRadius:12, padding:"13px 16px", color:"var(--success)", fontFamily:"var(--body)", fontWeight:600, fontSize:14, cursor: updating ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:12, transition:".15s", opacity: updating ? .6 : 1 }}
                        onClick={closeOutJob}
                        disabled={updating}>
                        <span style={{ fontSize:22, lineHeight:1 }}>💵</span>
                        <span style={{ flex:1, textAlign:"left" }}>
                          <span style={{ display:"block" }}>Cash / Already Paid</span>
                          <span style={{ fontSize:11, fontWeight:400, color:"var(--muted)" }}>Mark complete</span>
                        </span>
                        {updating
                          ? <span className="spinner" style={{ flexShrink:0 }}/>
                          : <span style={{ fontFamily:"var(--head)", fontSize:16, fontWeight:800, flexShrink:0 }}>{fmt$(total.toFixed(2))}</span>}
                      </button>

                      <button className="btn-close" style={{ marginTop:4 }} onClick={() => setJobStep("after")}>← Back to After Photos</button>
                    </div>
                  ) : (
                    /* ── Link sent — QR + confirm ── */
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:14 }}>
                      <div style={{ background:"rgba(0,201,167,.08)", border:"1px solid rgba(0,201,167,.25)", borderRadius:10, padding:"10px 18px", fontSize:13, color:"var(--teal)", fontWeight:600 }}>
                        ✓ Payment link sent to customer
                      </div>

                      <div style={{ background:"var(--card2)", border:"1px solid var(--border)", borderRadius:14, padding:14 }}>
                        <img src={qrUrl} alt="Payment QR" width={160} height={160} style={{ display:"block", borderRadius:8 }} />
                      </div>
                      <div style={{ fontSize:12, color:"var(--muted)" }}>Customer can scan to open their payment link</div>

                      <div style={{ display:"flex", gap:8, width:"100%" }}>
                        <button
                          style={{ flex:1, background:"var(--card2)", border:"1px solid var(--border)", borderRadius:9, padding:"10px", color:"var(--muted)", fontFamily:"var(--body)", fontSize:13, cursor:"pointer" }}
                          onClick={() => { navigator.clipboard.writeText(paymentUrl); showToast("Link copied! ✓"); }}>
                          📋 Copy Link
                        </button>
                        <button
                          style={{ flex:1, background:"var(--card2)", border:"1px solid var(--border)", borderRadius:9, padding:"10px", color:"var(--muted)", fontFamily:"var(--body)", fontSize:13, cursor:"pointer" }}
                          onClick={() => window.open(paymentUrl, "_blank")}>
                          🔗 Open Link
                        </button>
                      </div>

                      <button className="btn-primary" style={{ width:"100%" }} onClick={closeOutJob} disabled={updating}>
                        {updating ? <><span className="spinner"/> Closing out...</> : "✅ Mark as Paid & Close Job"}
                      </button>
                      <div style={{ fontSize:11, color:"var(--muted)" }}>Tap once the customer has completed payment</div>
                    </div>
                  )}
                </>
              );
            })()}

          </div>
        </div>
      )}

      {/* ── SERVICE EDIT / ADD MODAL ──────────────────── */}
      {svcModal !== null && (
        <div className="modal-overlay" onClick={() => setSvcModal(null)}>
          <div className="modal fade-in" onClick={e => e.stopPropagation()}>
            <h3>{svcModal === "new" ? "ADD SERVICE" : "EDIT SERVICE"}</h3>
            <form onSubmit={saveService}>
              <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:20 }}>
                <div style={{ display:"grid", gridTemplateColumns:"60px 1fr", gap:10 }}>
                  <div className="form-group">
                    <label>Icon</label>
                    <input type="text" value={svcForm.icon} onChange={e => setSvcForm(f=>({...f,icon:e.target.value}))} style={{ fontSize:22, textAlign:"center", padding:"10px 8px" }} />
                  </div>
                  <div className="form-group">
                    <label>Service Name</label>
                    <input type="text" placeholder="e.g. Full Detail" value={svcForm.name}
                      onChange={e => setSvcForm(f=>({...f,name:e.target.value}))} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input type="text" placeholder="What's included?" value={svcForm.description}
                    onChange={e => setSvcForm(f=>({...f,description:e.target.value}))} />
                </div>
                <div className="form-group">
                  <label>Duration</label>
                  <input type="text" placeholder="e.g. 2–3 hrs" value={svcForm.duration}
                    onChange={e => setSvcForm(f=>({...f,duration:e.target.value}))} />
                </div>
                <div>
                  <div style={{ fontSize:12, fontWeight:600, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>Pricing by Vehicle</div>
                  <div className="pricing-fields">
                    {VEHICLES.map(v => (
                      <div className="form-group" key={v.id}>
                        <label>{v.label}</label>
                        <input type="number" placeholder="0" value={svcForm.pricing[v.id]}
                          onChange={e => setSvcForm(f=>({...f,pricing:{...f.pricing,[v.id]:e.target.value}}))} min="0" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button type="button" className="btn-close" onClick={() => setSvcModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary" style={{ flex:1 }} disabled={updating}>
                  {updating ? <><span className="spinner"/> Saving...</> : "Save Service"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── INVENTORY EDIT / ADD MODAL (Pro+) ─────────── */}
      {invModal !== null && (
        <div className="modal-overlay" onClick={() => setInvModal(null)}>
          <div className="modal fade-in" onClick={e => e.stopPropagation()}>
            <h3>{invModal === "new" ? "ADD INVENTORY ITEM" : "EDIT ITEM"}</h3>
            <form onSubmit={saveInventory}>
              <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:20 }}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="e.g. Ceramic Wax" value={invForm.name}
                    onChange={e => setInvForm(f => ({ ...f, name:e.target.value }))} required />
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div className="form-group">
                    <label>Quantity</label>
                    <input type="number" step="0.01" min="0" placeholder="0" value={invForm.quantity}
                      onChange={e => setInvForm(f => ({ ...f, quantity:e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Unit</label>
                    <input type="text" list="inv-units" placeholder="each" value={invForm.unit}
                      onChange={e => setInvForm(f => ({ ...f, unit:e.target.value }))} />
                    <datalist id="inv-units">
                      <option value="each" />
                      <option value="oz" />
                      <option value="fl oz" />
                      <option value="gal" />
                      <option value="ml" />
                      <option value="l" />
                      <option value="lb" />
                      <option value="kg" />
                      <option value="pack" />
                      <option value="box" />
                      <option value="case" />
                    </datalist>
                  </div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div className="form-group">
                    <label>Cost / unit <span style={{ color:"var(--muted)", fontWeight:400 }}>(optional)</span></label>
                    <input type="number" step="0.01" min="0" placeholder="—" value={invForm.cost_per_unit}
                      onChange={e => setInvForm(f => ({ ...f, cost_per_unit:e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Low-stock at <span style={{ color:"var(--muted)", fontWeight:400 }}>(optional)</span></label>
                    <input type="number" step="0.01" min="0" placeholder="—" value={invForm.low_stock_threshold}
                      onChange={e => setInvForm(f => ({ ...f, low_stock_threshold:e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Notes <span style={{ color:"var(--muted)", fontWeight:400 }}>(optional)</span></label>
                  <input type="text" placeholder="brand, supplier, etc." value={invForm.notes}
                    onChange={e => setInvForm(f => ({ ...f, notes:e.target.value }))} />
                </div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button type="button" className="btn-close" onClick={() => setInvModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary" style={{ flex:1 }} disabled={updating}>
                  {updating ? <><span className="spinner"/> Saving...</> : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && <Toast {...toast} />}
    </>
  );
}
